import React, { useState, useEffect } from 'react';
import { loadClients, updateClient } from '../utils/storage';
import { apiFetch, getFileUrl } from '../utils/apiUtils.js';
import { formatDateOnly, formatDateTime, parseLocalDate } from '../utils/dateUtils';

// Nota: Los componentes de secciones no se importan aquí porque no se usan directamente

// Función para determinar si un rol requiere filtrado por células
const requiresCellFiltering = (role) => {
  return role === 'standard' || role === 'executive';
};

const AllTasksSection = ({ currentUser }) => {
  const [allClients, setAllClients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [filterAssignedTo, setFilterAssignedTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [viewMode, setViewMode] = useState('cards');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Nuevos filtros avanzados
  const [filterClient, setFilterClient] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterArea, setFilterArea] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterDateType, setFilterDateType] = useState('created'); // 'created' o 'due'
  
  // Estados para el calendario
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarViewType, setCalendarViewType] = useState('Mes');
  
  // Estados para filtros de células
  const [showCellsMenu, setShowCellsMenu] = useState(true);
  const [selectedCells, setSelectedCells] = useState([]);
  
  // Estados para modales
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showUserHistoryModal, setShowUserHistoryModal] = useState(false);
  const [showClientHistoryModal, setShowClientHistoryModal] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  
  // Estados para nueva tarea
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    points: [''],
    assignedTo: '',
    dueDate: '',
    priority: 'media',
    clientId: '',
    meetingId: '',
    publicityAccountLink: '',
    websiteLink: '',
    cell: '',
    platform: '',
    area: '',
    evidenceType: '',
    audioExplanation: '',
    evidenceFile: null,
    evidenceFileName: '',
    evidenceFileSize: 0,
    evidenceLink: ''
  });

  // Estados para buscadores en tiempo real
  const [userSearch, setUserSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Estados para editar tarea
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  // Estados para historial de usuario
  const [userHistoryFilters, setUserHistoryFilters] = useState({
    selectedUser: '',
    dateFrom: '',
    dateTo: '',
    status: 'all'
  });
  const [userHistoryTasks, setUserHistoryTasks] = useState([]);

  // Estados para historial de cliente
  const [clientHistoryFilters, setClientHistoryFilters] = useState({
    selectedClient: '',
    files: []
  });
  const [clientHistoryData, setClientHistoryData] = useState({
    tasks: [],
    monthlyStats: {},
    files: []
  });
  
  // Estados para gestión de archivos
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Estados para filtros avanzados
  const [advancedFilters, setAdvancedFilters] = useState({
    assignedTo: 'Todos',
    cell: 'Todas',
    platform: 'Todas',
    area: 'Todas',
    dateFrom: '',
    dateTo: '',
    creationFilter: 'all' // 'today', 'week', 'month', 'custom'
  });

  // Estados para usuarios del sistema
  const [systemUsers, setSystemUsers] = useState([]);

  // Estados para grabación de audio
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);

  // Estados para modal de imagen
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Estados para transcripción de audio
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [selectedTaskForTranscription, setSelectedTaskForTranscription] = useState(null);
  const [transcriptionResults, setTranscriptionResults] = useState({});


  // Estados para comentarios de tareas
  const [taskComments, setTaskComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});
  const [commentFiles, setCommentFiles] = useState({}); // Archivos para comentarios
  const [commentFileNames, setCommentFileNames] = useState({}); // Nombres personalizados

  // Estados para notificaciones
  const [notifications, setNotifications] = useState([]);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastCheckTime, setLastCheckTime] = useState(() => {
    // Cargar última vez que se revisaron las notificaciones
    const saved = localStorage.getItem(`lastNotificationCheck_${currentUser?.id}`);
    return saved ? new Date(saved) : new Date();
  });

  // Estados para modal de detalles de tarea
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [selectedTaskForDetails, setSelectedTaskForDetails] = useState(null);

  // Función para cargar notificaciones
  const loadNotifications = async () => {
    try {
      // Obtener tareas nuevas desde la última revisión
      const newTasks = tasks.filter(task => {
        const taskDate = new Date(task.createdAt || task.meetingDate);
        return taskDate > lastCheckTime && task.assignedTo === `${currentUser?.firstName} ${currentUser?.lastName}`;
      });

      // Obtener comentarios nuevos en tareas asignadas al usuario
      const userTasks = tasks.filter(task => task.assignedTo === `${currentUser?.firstName} ${currentUser?.lastName}`);
      const newComments = [];
      
      for (const task of userTasks) {
        if (taskComments[task.id]) {
          const taskNewComments = taskComments[task.id].filter(comment => {
            const commentDate = new Date(comment.createdAt || comment.created_at);
            const commentUser = comment.userName || comment.user_name;
            const currentUserName = `${currentUser?.firstName} ${currentUser?.lastName}`;
            
            // Solo comentarios nuevos que no sean del usuario actual
            return commentDate > lastCheckTime && commentUser !== currentUserName;
          });
          
          taskNewComments.forEach(comment => {
            newComments.push({
              ...comment,
              taskId: task.id,
              taskDescription: task.description,
              clientName: task.clientName
            });
          });
        }
      }

      // Crear notificaciones
      const taskNotifications = newTasks.map(task => ({
        id: `task-${task.id}`,
        type: 'new_task',
        title: 'Nueva tarea asignada',
        message: `Se te ha asignado una nueva tarea: ${task.description}`,
        clientName: task.clientName,
        taskId: task.id,
        priority: task.priority,
        createdAt: task.createdAt || task.meetingDate,
        isRead: false
      }));

      const commentNotifications = newComments.map(comment => ({
        id: `comment-${comment.id}`,
        type: 'new_comment',
        title: 'Nuevo comentario en tu tarea',
        message: `${comment.userName || comment.user_name} comentó en: ${comment.taskDescription}`,
        clientName: comment.clientName,
        taskId: comment.taskId,
        commentText: comment.comment,
        createdAt: comment.createdAt || comment.created_at,
        isRead: false
      }));

      const allNotifications = [...taskNotifications, ...commentNotifications]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setNotifications(allNotifications);
      setUnreadCount(allNotifications.length);

    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  // Función para marcar notificaciones como leídas
  const markNotificationsAsRead = () => {
    const now = new Date();
    localStorage.setItem(`lastNotificationCheck_${currentUser?.id}`, now.toISOString());
    setLastCheckTime(now);
    setUnreadCount(0);
    setNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })));
  };

  // Función para ir a una tarea específica desde notificación
  const goToTask = (taskId) => {
    // Buscar la tarea y hacer scroll hacia ella
    const taskElement = document.querySelector(`[data-task-id="${taskId}"]`);
    if (taskElement) {
      taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      taskElement.classList.add('highlight-task');
      setTimeout(() => {
        taskElement.classList.remove('highlight-task');
      }, 3000);
    }
    setShowNotificationsModal(false);
  };

  // Obtener todos los usuarios para el filtro de asignación (de tareas existentes)
  const allUsers = Array.from(new Set(
    (Array.isArray(allClients) ? allClients : []).flatMap(client => 
      (client.meetings || []).flatMap(meeting => 
        (meeting.tasks || []).map(task => task.assignedTo)
      )
    )
  )).filter(Boolean);

  // Función para recargar datos desde el servidor
  const reloadData = async () => {
    try {
      console.log('🔄 AllTasksSection - Iniciando recarga de datos...');
      console.log('👤 Usuario actual:', currentUser?.firstName, currentUser?.lastName, 'Rol:', currentUser?.role);
      
      // 1. Cargar usuarios del sistema
      try {
        const usersResponse = await apiFetch('/users');
        if (usersResponse.ok) {
          const users = await usersResponse.json();
          console.log('👥 Usuarios cargados:', users.length);
          setSystemUsers(users);
        }
      } catch (error) {
        console.error('Error loading users:', error);
      }
      
      // 2. Cargar TODOS los clientes (sin filtro por células)
      let clientsData = [];
      try {
        console.log('🔍 AllTasksSection - Cargando TODOS los clientes (sin filtro por células)');
        
        const clientsResponse = await apiFetch('/clients');
        if (clientsResponse.ok) {
          clientsData = await clientsResponse.json();
          console.log('👥 Clientes cargados del servidor (todos):', clientsData.length);
          setAllClients(clientsData);
        }
      } catch (error) {
        console.error('Error loading clients:', error);
      }
      
      // 3. Cargar tareas del servidor CON FILTRO POR USUARIO ASIGNADO
      try {
        console.log('🔍 AllTasksSection - Cargando tareas del servidor');
        
        const tasksResponse = await apiFetch('/tasks');
        if (tasksResponse.ok) {
          let serverTasks = await tasksResponse.json();
          console.log('📋 Tareas cargadas del servidor (todas):', serverTasks.length);
          
          // FILTRAR INMEDIATAMENTE POR USUARIO ASIGNADO (para no-admin)
          if (currentUser?.role !== 'admin') {
            const currentUserFullName = `${currentUser.firstName} ${currentUser.lastName}`;
            console.log('🔒 Filtrando tareas para usuario NO-ADMIN:', currentUserFullName);
            
            serverTasks = serverTasks.filter(task => {
              const isAssigned = task.assignedTo === currentUserFullName;
              if (!isAssigned) {
                console.log(`❌ Tarea ${task.id} filtrada - asignada a: ${task.assignedTo}`);
              }
              return isAssigned;
            });
            
            console.log('✅ Tareas después del filtro por usuario:', serverTasks.length);
          } else {
            console.log('👑 Usuario ADMIN - sin filtro por usuario asignado');
          }
          
          // Procesar tareas con información de clientes
          const processedTasks = serverTasks.map(task => {
            const client = clientsData.find(c => c.id == task.clientId || c.id === parseInt(task.clientId));
            
            return {
              ...task,
              clientName: client ? client.name : `Cliente ${task.clientId}`,
              meetingDate: task.createdAt || new Date().toISOString(),
              meetingType: task.meetingId && typeof task.meetingId === 'string' && task.meetingId.startsWith('virtual-') ? 'Tarea independiente' : 'Tarea del sistema',
              meetingId: task.meetingId || `virtual-${task.id}`,
              priority: task.priority || 'media',
              status: task.completed ? 'completada' : 'pendiente',
              cell: task.cell || client?.cell || 'C1GA',
              id: task.id,
              title: task.title || task.description,
              description: task.description || task.title,
              points: task.points || task.description,
              assignedTo: task.assignedTo,
              dueDate: task.dueDate,
              createdAt: task.createdAt,
              updatedAt: task.updatedAt || task.createdAt,
              completed: task.completed || false,
              approved: task.approved || false
            };
          });
          
          console.log('📋 Tareas procesadas finales:', processedTasks.length);
          
          // Guardar tareas YA FILTRADAS
          setTasks(processedTasks);
          
        } else {
          console.error('Error al cargar tareas del servidor');
          setTasks([]);
        }
      } catch (error) {
        console.error('Error loading tasks from server:', error);
        setTasks([]);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      setAllClients([]);
      setTasks([]);
    }
  };

  useEffect(() => {
    reloadData();
  }, [currentUser]);

  // Filtrar y ordenar tareas usando useMemo (filtrado por usuario asignado)
  const filteredAndSortedTasks = React.useMemo(() => {
    console.log('🔄 AllTasksSection - Iniciando filtrado de tareas...');
    console.log('👤 Usuario actual:', currentUser?.firstName, currentUser?.lastName, 'Rol:', currentUser?.role);
    console.log('📊 Total de tareas recibidas:', tasks.length);
    
    let currentFiltered = [...tasks];

    // FILTRAR TAREAS POR USUARIO ASIGNADO (OBLIGATORIO PARA NO-ADMIN)
    if (currentUser?.role !== 'admin') {
  // Para usuarios no administradores, SIEMPRE filtrar por usuario asignado
  if (currentUser?.firstName && currentUser?.lastName) {
    const currentUserFullName = `${currentUser.firstName} ${currentUser.lastName}`;
    
    currentFiltered = currentFiltered.filter(task => {
      const isAssignedToUser = task.assignedTo === currentUserFullName;
      return isAssignedToUser;
    });
  }
} else {
      console.log('👑 Usuario ADMIN: Mostrando TODAS las tareas sin filtro por usuario');
    }

    // Aplicar filtro de búsqueda
    if (searchTerm && searchTerm.trim()) {
      const beforeSearchFilter = currentFiltered.length;
      const searchLower = searchTerm.toLowerCase().trim();
      currentFiltered = currentFiltered.filter(task => 
        task.description?.toLowerCase().includes(searchLower) ||
        task.clientName?.toLowerCase().includes(searchLower) ||
        task.assignedTo?.toLowerCase().includes(searchLower)
      );
      console.log(`🔍 Tareas después del filtro de búsqueda: ${currentFiltered.length} (filtradas: ${beforeSearchFilter - currentFiltered.length})`);
    }

    // Aplicar filtro de usuario asignado (adicional - para filtros específicos)
    if (filterAssignedTo) {
      const beforeUserFilter = currentFiltered.length;
      currentFiltered = currentFiltered.filter(task => task.assignedTo === filterAssignedTo);
      console.log(`👤 Tareas después del filtro de usuario específico: ${currentFiltered.length} (filtradas: ${beforeUserFilter - currentFiltered.length})`);
    }

    // Aplicar filtro de células seleccionadas (OPCIONAL - solo si hay células seleccionadas explícitamente)
    if (selectedCells && selectedCells.length > 0) {
      const beforeCellsFilter = currentFiltered.length;
      console.log(`🏢 Aplicando filtro OPCIONAL de células seleccionadas: ${selectedCells.join(', ')}`);
      
      currentFiltered = currentFiltered.filter(task => {
        const taskCell = task.cell;
        const taskStatus = task.completed || task.status === 'completada' ? 'Realizadas' : 'Pendientes';
        
        return selectedCells.some(cellFilter => {
          const [filterStatus, filterCell] = cellFilter.split(' ');
          return filterStatus === taskStatus && filterCell === taskCell;
        });
      });
      console.log(`🏢 Tareas después del filtro OPCIONAL de células: ${currentFiltered.length} (filtradas: ${beforeCellsFilter - currentFiltered.length})`);
    } else {
      console.log(`🏢 Sin filtro de células - mostrando TODAS las tareas asignadas al usuario`);
    }

    // Aplicar filtros de estado - CORREGIDO PARA EVITAR DUPLICADOS
    if (filterStatus === 'all') {
      // Mostrar todas las tareas sin filtro de estado
      console.log('📋 Filtro "Todas" - sin filtro de estado');
    } else if (filterStatus === 'new') {
      const beforeStatusFilter = currentFiltered.length;
      currentFiltered = currentFiltered.filter(task => !task.completed && task.status !== 'completada' && task.status !== 'cancelada' && !task.approved && task.status !== 'aprobada');
      console.log(`🆕 Tareas después del filtro "Tareas Nuevas": ${currentFiltered.length} (filtradas: ${beforeStatusFilter - currentFiltered.length})`);
    } else if (filterStatus === 'completed') {
      // CLAVE: Excluir tareas aprobadas de "Realizadas"
      const beforeStatusFilter = currentFiltered.length;
      currentFiltered = currentFiltered.filter(task => (task.completed || task.status === 'completada') && !task.approved && task.status !== 'aprobada');
      console.log(`✅ Tareas después del filtro "Tareas Realizadas" (sin aprobadas): ${currentFiltered.length} (filtradas: ${beforeStatusFilter - currentFiltered.length})`);
    } else if (filterStatus === 'approved') {
      const beforeStatusFilter = currentFiltered.length;
      currentFiltered = currentFiltered.filter(task => task.approved || task.status === 'aprobada');
      console.log(`👍 Tareas después del filtro "Tareas Aprobadas": ${currentFiltered.length} (filtradas: ${beforeStatusFilter - currentFiltered.length})`);
    } else if (filterStatus === 'pending') {
      const beforeStatusFilter = currentFiltered.length;
      currentFiltered = currentFiltered.filter(task => !task.completed && task.status !== 'completada' && !task.approved && task.status !== 'aprobada');
      console.log(`⏳ Tareas después del filtro "Pendientes": ${currentFiltered.length} (filtradas: ${beforeStatusFilter - currentFiltered.length})`);
    }

    // Aplicar filtro de prioridad
    if (filterPriority !== 'all') {
      const beforePriorityFilter = currentFiltered.length;
      currentFiltered = currentFiltered.filter(task => task.priority === filterPriority);
      console.log(`🎯 Tareas después del filtro de prioridad: ${currentFiltered.length} (filtradas: ${beforePriorityFilter - currentFiltered.length})`);
    }

    // Ordenar tareas: de más antiguas a más nuevas (por fecha de creación)
    currentFiltered.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(a.meetingDate);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(b.meetingDate);
      return dateA - dateB; // Más antiguas primero
    });

    console.log('✅ Tareas finales después de todos los filtros:', currentFiltered.length);
    return currentFiltered;
  }, [tasks, searchTerm, filterAssignedTo, filterStatus, filterPriority, selectedCells, currentUser, allClients]);

  // Función para aplicar filtros a las tareas
  const applyFilters = (tasksToFilter, assignedToFilter, statusFilter, priorityFilter, searchFilter, cellsFilter) => {
    console.log('🔍 Aplicando filtros:', {
      assignedToFilter,
      statusFilter,
      priorityFilter,
      searchFilter,
      cellsFilter: cellsFilter?.length || 0
    });
    
    let filtered = [...tasksToFilter];
    console.log('📊 Tareas iniciales:', filtered.length);
    
    // Filtro de búsqueda
    if (searchFilter && searchFilter.trim()) {
      const searchLower = searchFilter.toLowerCase().trim();
      filtered = filtered.filter(task => 
        task.description?.toLowerCase().includes(searchLower) ||
        task.clientName?.toLowerCase().includes(searchLower) ||
        task.assignedTo?.toLowerCase().includes(searchLower)
      );
      console.log('🔍 Después de filtro de búsqueda:', filtered.length);
    }
    
    // Filtro de usuario asignado
    if (assignedToFilter) {
      filtered = filtered.filter(task => task.assignedTo === assignedToFilter);
      console.log('👤 Después de filtro de usuario:', filtered.length);
    }
    
    // Filtro de células
    if (cellsFilter && cellsFilter.length > 0) {
      filtered = filtered.filter(task => {
        const taskCell = task.cell;
        const taskStatus = task.completed || task.status === 'completada' ? 'Realizadas' : 'Pendientes';
        
        return cellsFilter.some(cellFilter => {
          const [filterStatus, filterCell] = cellFilter.split(' ');
          return filterStatus === taskStatus && filterCell === taskCell;
        });
      });
      console.log('🏢 Después de filtro de células:', filtered.length);
    }
    
    // Filtros de estado - CORREGIDO PARA EVITAR DUPLICADOS
    if (statusFilter === 'all') {
      // Mostrar todas las tareas sin filtro de estado
      console.log('📋 Filtro "Todas" - sin filtro de estado');
    } else if (statusFilter === 'new') {
      filtered = filtered.filter(task => !task.completed && task.status !== 'completada' && task.status !== 'cancelada' && !task.approved && task.status !== 'aprobada');
      console.log('🆕 Después de filtro "Tareas Nuevas":', filtered.length);
    } else if (statusFilter === 'completed') {
      // CLAVE: Excluir tareas aprobadas de "Realizadas"
      filtered = filtered.filter(task => (task.completed || task.status === 'completada') && !task.approved && task.status !== 'aprobada');
      console.log('✅ Después de filtro "Tareas Realizadas" (sin aprobadas):', filtered.length);
    } else if (statusFilter === 'approved') {
      filtered = filtered.filter(task => task.approved || task.status === 'aprobada');
      console.log('👍 Después de filtro "Tareas Aprobadas":', filtered.length);
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter(task => !task.completed && task.status !== 'completada' && !task.approved && task.status !== 'aprobada');
      console.log('⏳ Después de filtro "Pendientes":', filtered.length);
    }
    
    // Filtro de prioridad
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(task => task.priority === priorityFilter);
      console.log('🎯 Después de filtro de prioridad:', filtered.length);
    }

    // Ordenar tareas: de más antiguas a más nuevas (por fecha de creación)
    filtered.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(a.meetingDate);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(b.meetingDate);
      return dateA - dateB; // Más antiguas primero
    });

    console.log('✅ Tareas finales después de filtros:', filtered.length);
    return filtered;
  };

  const processAndFilterTasks = (clientsToProcess, assignedToFilter, statusFilter, priorityFilter, searchFilter, cellsFilter) => {
    let collectedTasks = [];
    const clients = Array.isArray(clientsToProcess) ? clientsToProcess : [];
    
    clients.forEach(client => {
      if (client.meetings && Array.isArray(client.meetings)) {
        client.meetings.forEach(meeting => {
          if (meeting.tasks && Array.isArray(meeting.tasks)) {
            meeting.tasks.forEach(task => {
              // Asignar célula simulada basada en el cliente o tarea
              const simulatedCell = task.cell || client.cell || ['C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA', 'C7GA', 'C8GA', 'C1MA', 'C2MA', 'C3MA', 'C4MA', 'C5MA'][Math.floor(Math.random() * 13)];
              
              collectedTasks.push({
                ...task,
                clientId: client.id,
                clientName: client.name,
                meetingDate: meeting.date,
                meetingType: meeting.meetingType,
                meetingId: meeting.id,
                priority: task.priority || 'media',
                status: task.status || (task.completed ? 'completada' : 'pendiente'),
                cell: simulatedCell
              });
            });
          }
        });
      }
    });

    // Aplicar filtros
    let filtered = collectedTasks;
    
    // Filtro de búsqueda
    if (searchFilter && searchFilter.trim()) {
      const searchLower = searchFilter.toLowerCase().trim();
      filtered = filtered.filter(task => 
        task.description?.toLowerCase().includes(searchLower) ||
        task.clientName?.toLowerCase().includes(searchLower) ||
        task.assignedTo?.toLowerCase().includes(searchLower)
      );
    }
    
    if (assignedToFilter) {
      filtered = filtered.filter(task => task.assignedTo === assignedToFilter);
    }
    
    // Filtro de células
    if (cellsFilter && cellsFilter.length > 0) {
      filtered = filtered.filter(task => {
        const taskCell = task.cell;
        const taskStatus = task.completed || task.status === 'completada' ? 'Realizadas' : 'Pendientes';
        
        // Verificar si algún filtro de célula coincide
        return cellsFilter.some(cellFilter => {
          const [filterStatus, filterCell] = cellFilter.split(' ');
          return filterStatus === taskStatus && filterCell === taskCell;
        });
      });
    }
    
    // Filtros de estado de la nueva barra de navegación
    if (statusFilter === 'new') {
      filtered = filtered.filter(task => !task.completed && task.status !== 'completada' && task.status !== 'cancelada');
    } else if (statusFilter === 'completed') {
      filtered = filtered.filter(task => task.completed || task.status === 'completada');
    } else if (statusFilter === 'approved') {
      filtered = filtered.filter(task => task.status === 'aprobada');
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter(task => !task.completed && task.status !== 'completada');
    }
    
    if (priorityFilter !== 'all') {
      filtered = filtered.filter(task => task.priority === priorityFilter);
    }

    // Ordenar tareas: de más antiguas a más nuevas (por fecha de creación)
    filtered.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(a.meetingDate);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(b.meetingDate);
      return dateA - dateB; // Más antiguas primero
    });

    setTasks(filtered);
  };

  const handleTaskCompletion = async (taskId, meetingId, clientId, isCompleted, comment) => {
    try {
      // Actualizar la tarea en el servidor
      const response = await apiFetch(`/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          completed: isCompleted ? 1 : 0,
          completionComment: comment,
          completedAt: isCompleted ? new Date().toISOString() : null,
          completedBy: isCompleted ? currentUser?.name || 'Usuario' : null
        })
      });

      if (!response.ok) {
        throw new Error('Error al actualizar la tarea en el servidor');
      }

      // Recargar todas las tareas desde el servidor para asegurar sincronización
      try {
        const tasksResponse = await apiFetch('/tasks');
        if (tasksResponse.ok) {
          const serverTasks = await tasksResponse.json();
          
          // Procesar tareas del servidor con información de clientes
          const processedTasks = serverTasks.map(task => {
            const client = allClients.find(c => c.id === task.clientId);
            return {
              ...task,
              clientName: client ? client.name : `Cliente ${task.clientId}`,
              meetingDate: task.createdAt || new Date().toISOString(),
              meetingType: 'Tarea del sistema',
              meetingId: task.meetingId || task.id,
              priority: task.priority || 'media',
              status: task.completed ? 'completada' : 'pendiente',
              cell: task.cell || 'C1GA'
            };
          });
          
          // Aplicar filtros actuales a las tareas actualizadas
          let filteredTasks = processedTasks;
          
          // Filtro de búsqueda
          if (searchTerm && searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            filteredTasks = filteredTasks.filter(task => 
              task.description?.toLowerCase().includes(searchLower) ||
              task.clientName?.toLowerCase().includes(searchLower) ||
              task.assignedTo?.toLowerCase().includes(searchLower)
            );
          }
          
          if (filterAssignedTo) {
            filteredTasks = filteredTasks.filter(task => task.assignedTo === filterAssignedTo);
          }
          
          // Filtro de células
          if (selectedCells && selectedCells.length > 0) {
            filteredTasks = filteredTasks.filter(task => {
              const taskCell = task.cell;
              const taskStatus = task.completed || task.status === 'completada' ? 'Realizadas' : 'Pendientes';
              
              return selectedCells.some(cellFilter => {
                const [filterStatus, filterCell] = cellFilter.split(' ');
                return filterStatus === taskStatus && filterCell === taskCell;
              });
            });
          }
          
          // Filtros de estado - CORREGIDO
          const currentFilter = isCompleted && filterStatus !== 'completed' ? 'completed' : filterStatus;
          
          if (currentFilter === 'new') {
            filteredTasks = filteredTasks.filter(task => !task.completed && task.status !== 'completada' && task.status !== 'cancelada');
          } else if (currentFilter === 'completed') {
            filteredTasks = filteredTasks.filter(task => task.completed || task.status === 'completada');
          } else if (currentFilter === 'approved') {
            filteredTasks = filteredTasks.filter(task => task.status === 'aprobada');
          } else if (currentFilter === 'pending') {
            filteredTasks = filteredTasks.filter(task => !task.completed && task.status !== 'completada');
          }
          
          if (filterPriority !== 'all') {
            filteredTasks = filteredTasks.filter(task => task.priority === filterPriority);
          }

          // Ordenar tareas
          filteredTasks.sort((a, b) => {
            const dateA = a.createdAt ? new Date(a.createdAt) : new Date(a.meetingDate);
            const dateB = b.createdAt ? new Date(b.createdAt) : new Date(b.meetingDate);
            return dateA - dateB;
          });

          setTasks(filteredTasks);
          
          // Si la tarea se marca como completada, cambiar automáticamente a "Tareas Realizadas"
          if (isCompleted && filterStatus !== 'completed') {
            setFilterStatus('completed');
          }
        }
      } catch (error) {
        console.error('Error reloading tasks:', error);
      }
      
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Error al actualizar la tarea: ' + error.message);
    }
  };


  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgente': return 'bg-red-100 text-red-800 border-red-200';
      case 'alta': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'media': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'baja': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status, completed) => {
    if (completed || status === 'completada') return 'bg-green-100 text-green-800 border-green-200';
    if (status === 'en_progreso') return 'bg-blue-100 text-blue-800 border-blue-200';
    if (status === 'cancelada') return 'bg-red-100 text-red-800 border-red-200';
    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  };

  // Función para calcular el estado real de la tarea basado en fechas
  const getTaskRealStatus = (task) => {
    if (task.completed || task.status === 'completada') return { text: '✅ Completada', type: 'completed' };
    if (task.status === 'cancelada') return { text: '❌ Cancelada', type: 'cancelled' };
    if (task.status === 'en_progreso') return { text: '🔄 En Progreso', type: 'in_progress' };

    const now = new Date();
    const createdDate = task.createdAt ? new Date(task.createdAt) : new Date(task.meetingDate);
    const dueDate = task.dueDate ? new Date(task.dueDate) : null;
    
    // Normalizar fechas a medianoche para cálculo preciso de días
    const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const createdDateNormalized = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());
    const dueDateNormalized = dueDate ? new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()) : null;
    
    // Calcular días desde la creación
    const daysSinceCreated = Math.floor((nowDate - createdDateNormalized) / (1000 * 60 * 60 * 24));
    
    // Si tiene fecha límite, verificar si está vencida
    if (dueDateNormalized) {
      const daysUntilDue = Math.floor((dueDateNormalized - nowDate) / (1000 * 60 * 60 * 24));
      
      if (daysUntilDue < 0) {
        const daysOverdue = Math.abs(daysUntilDue);
        if (daysOverdue === 1) {
          return { text: '🔴 Vencida (1 día de retraso)', type: 'overdue_critical' };
        } else if (daysOverdue <= 3) {
          return { text: `🔴 Vencida (${daysOverdue} días de retraso)`, type: 'overdue_critical' };
        } else if (daysOverdue <= 7) {
          return { text: `🔴 CRÍTICA (${daysOverdue} días de retraso)`, type: 'overdue_critical' };
        } else {
          return { text: `🔴 URGENTE (${daysOverdue} días de retraso)`, type: 'overdue_critical' };
        }
      } else if (daysUntilDue === 0) {
        return { text: '🟡 Vence Hoy', type: 'due_today' };
      } else if (daysUntilDue === 1) {
        return { text: '🟡 Vence Mañana', type: 'due_soon' };
      }
    }
    
    // Clasificar por días desde creación si no está vencida
    if (daysSinceCreated === 0) {
      return { text: '🟢 Nueva (Hoy)', type: 'new_today' };
    } else if (daysSinceCreated === 1) {
      return { text: '🟡 1 día', type: 'one_day' };
    } else if (daysSinceCreated <= 3) {
      return { text: `🟡 ${daysSinceCreated} días`, type: 'few_days' };
    } else if (daysSinceCreated <= 7) {
      return { text: `🟠 ${daysSinceCreated} días (Revisar)`, type: 'week_old' };
    } else if (daysSinceCreated <= 14) {
      return { text: `🔴 ${daysSinceCreated} días (Atención)`, type: 'two_weeks' };
    } else {
      return { text: `🔴 ${daysSinceCreated} días (URGENTE)`, type: 'very_old' };
    }
  };

  const getStatusText = (status, completed) => {
    if (completed || status === 'completada') return '✅ Completada';
    if (status === 'en_progreso') return '🔄 En Progreso';
    if (status === 'cancelada') return '❌ Cancelada';
    return '⏳ Pendiente';
  };

  // Función para obtener el color del estado real
  const getRealStatusColor = (statusType) => {
    switch (statusType) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'new_today': return 'bg-green-100 text-green-800 border-green-200';
      case 'one_day': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'few_days': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'week_old': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'two_weeks': return 'bg-red-100 text-red-800 border-red-200';
      case 'very_old': return 'bg-red-100 text-red-800 border-red-200';
      case 'due_today': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'due_soon': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'overdue_critical': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Función para calcular el tiempo de realización
  const calculateCompletionTime = (task) => {
    if (!task.completed && task.status !== 'completada') {
      return null; // No está completada
    }

    const createdDate = task.createdAt ? new Date(task.createdAt) : new Date(task.meetingDate);
    const completedDate = task.completedAt ? new Date(task.completedAt) : new Date();

    const timeDiff = completedDate - createdDate;
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days} día${days !== 1 ? 's' : ''}, ${hours} hora${hours !== 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `${hours} hora${hours !== 1 ? 's' : ''}, ${minutes} minuto${minutes !== 1 ? 's' : ''}`;
    } else {
      return `${minutes} minuto${minutes !== 1 ? 's' : ''}`;
    }
  };

  // Función para manejar la aprobación de tareas (solo administradores)
  const handleTaskApproval = async (taskId, meetingId, clientId, approved) => {
    if (currentUser?.role !== 'admin') {
      alert('Solo los administradores pueden aprobar tareas');
      return;
    }

    try {
      console.log('🔧 Aprobando/rechazando tarea:', { taskId, approved });
      
      // ACTUALIZAR ESTADO INMEDIATAMENTE EN EL FRONTEND
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { 
                ...task, 
                approved: approved,
                status: approved ? 'aprobada' : (task.completed ? 'completada' : 'pendiente'),
                approvedAt: approved ? new Date().toISOString() : null,
                approvedBy: approved ? `${currentUser?.firstName} ${currentUser?.lastName}` || currentUser?.name || 'Admin' : null
              }
            : task
        )
      );
      
      // Actualizar la tarea en el servidor usando las nuevas columnas
      const response = await apiFetch(`/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // Si se aprueba, automáticamente marcar como completada
          completed: approved ? 1 : undefined, // Solo actualizar si se aprueba
          approved: approved ? 1 : 0,
          approvedAt: approved ? (() => {
            const now = new Date();
            const offset = now.getTimezoneOffset() * 60000; // offset en milisegundos
            const localTime = new Date(now.getTime() - offset);
            return localTime.toISOString().slice(0, 19).replace('T', ' ');
          })() : null,
          approvedBy: approved ? `${currentUser?.firstName} ${currentUser?.lastName}` || currentUser?.name || 'Admin' : null,
          rejectedAt: !approved ? (() => {
            const now = new Date();
            const offset = now.getTimezoneOffset() * 60000; // offset en milisegundos
            const localTime = new Date(now.getTime() - offset);
            return localTime.toISOString().slice(0, 19).replace('T', ' ');
          })() : null
        })
      });

      console.log('📡 Respuesta del servidor:', response.status);

      if (!response.ok) {
        const errorData = await response.text();
        console.error('❌ Error del servidor:', errorData);
        
        // REVERTIR CAMBIO EN CASO DE ERROR
        setTasks(prevTasks => 
          prevTasks.map(task => 
            task.id === taskId 
              ? { 
                  ...task, 
                  approved: !approved, // Revertir
                  status: !approved ? 'aprobada' : (task.completed ? 'completada' : 'pendiente')
                }
              : task
          )
        );
        
        throw new Error('Error al actualizar el estado de aprobación en el servidor');
      }

      console.log('✅ Tarea actualizada en el servidor');
      
      alert(`Tarea ${approved ? 'aprobada' : 'rechazada'} exitosamente`);
      
      // Recargar datos en segundo plano para sincronización
      setTimeout(async () => {
        try {
          await reloadData();
        } catch (reloadError) {
          console.error('Error reloading tasks in background:', reloadError);
        }
      }, 1000);
      
    } catch (error) {
      console.error('❌ Error updating task approval:', error);
      alert('Error al actualizar el estado de aprobación: ' + error.message);
    }
  };

  const getPriorityEmoji = (priority) => {
    switch (priority) {
      case 'urgente': return '🔴';
      case 'alta': return '🟠';
      case 'media': return '🟡';
      case 'baja': return '🟢';
      default: return '🟡';
    }
  };

  // Función para generar texto de tareas para WhatsApp
  const generateTasksText = (tasksToShare, title = "Tareas") => {
    let text = `📋 *${title}*\n\n`;
    
    if (tasksToShare.length === 0) {
      text += "No hay tareas para mostrar.";
      return text;
    }

    tasksToShare.forEach((task, index) => {
      const priorityEmoji = getPriorityEmoji(task.priority);
      const statusEmoji = task.completed ? '✅' : '⏳';
      const dueDate = task.dueDate ? formatDateOnly(task.dueDate) : 'Sin fecha';
      
      text += `${index + 1}. ${statusEmoji} *${task.description}*\n`;
      text += `   👤 Asignado: ${task.assignedTo || 'Sin asignar'}\n`;
      text += `   🏢 Cliente: ${task.clientName}\n`;
      text += `   ${priorityEmoji} Prioridad: ${task.priority?.charAt(0).toUpperCase() + task.priority?.slice(1)}\n`;
      text += `   📅 Fecha límite: ${dueDate}\n`;
      text += `   🔗 Célula: ${task.cell || 'Sin asignar'}\n\n`;
    });

    text += `📊 *Resumen:*\n`;
    text += `• Total: ${tasksToShare.length}\n`;
    text += `• Pendientes: ${tasksToShare.filter(t => !t.completed).length}\n`;
    text += `• Completadas: ${tasksToShare.filter(t => t.completed).length}\n`;
    text += `• Urgentes: ${tasksToShare.filter(t => t.priority === 'urgente').length}\n\n`;
    text += `🕐 Generado: ${new Date().toLocaleString('es-MX')}`;

    return text;
  };

  // Función para compartir por WhatsApp
  const shareViaWhatsApp = (tasksToShare, title) => {
    const text = generateTasksText(tasksToShare, title);
    const encodedText = encodeURIComponent(text);
    const whatsappUrl = `https://wa.me/?text=${encodedText}`;
    window.open(whatsappUrl, '_blank');
  };

  // Función para compartir todas las tareas
  const shareAllTasks = () => {
    shareViaWhatsApp(tasks, "Todas las Tareas");
  };

  // Función para compartir tareas filtradas
  const shareFilteredTasks = () => {
    const filteredTasks = tasks; // Ya están filtradas por los filtros actuales
    let title = "Tareas Filtradas";
    
    if (selectedCells.length > 0) {
      title += ` (${selectedCells.join(', ')})`;
    }
    
    if (filterStatus !== 'all') {
      const statusNames = {
        'new': 'Nuevas',
        'completed': 'Realizadas', 
        'approved': 'Aprobadas',
        'pending': 'Pendientes'
      };
      title += ` - ${statusNames[filterStatus] || filterStatus}`;
    }

    shareViaWhatsApp(filteredTasks, title);
  };

  // Función dinámica para compartir tareas
  const shareTasks = () => {
    // Verificar si hay filtros activos
    const hasActiveFilters = 
      selectedCells.length > 0 || 
      filterStatus !== 'all' || 
      filterAssignedTo !== '' || 
      filterPriority !== 'all' || 
      searchTerm.trim() !== '';

    if (hasActiveFilters) {
      shareFilteredTasks();
    } else {
      shareAllTasks();
    }
  };

  // Función para obtener el texto dinámico del botón
  const getShareButtonText = () => {
    const hasActiveFilters = 
      selectedCells.length > 0 || 
      filterStatus !== 'all' || 
      filterAssignedTo !== '' || 
      filterPriority !== 'all' || 
      searchTerm.trim() !== '';

    if (hasActiveFilters) {
      return "📱 Compartir Tareas Filtradas por WhatsApp";
    } else {
      return "📱 Compartir Todas las Tareas por WhatsApp";
    }
  };

  // Función para filtrar tareas por usuario
  const filterUserTasks = () => {
    if (!userHistoryFilters.selectedUser) {
      setUserHistoryTasks([]);
      return;
    }

    let filteredTasks = [];
    
    // Obtener todas las tareas del sistema
    allClients.forEach(client => {
      if (client.meetings && Array.isArray(client.meetings)) {
        client.meetings.forEach(meeting => {
          if (meeting.tasks && Array.isArray(meeting.tasks)) {
            meeting.tasks.forEach(task => {
              if (task.assignedTo === userHistoryFilters.selectedUser) {
                const taskWithCell = {
                  ...task,
                  clientId: client.id,
                  clientName: client.name,
                  meetingDate: meeting.date,
                  meetingType: meeting.meetingType,
                  meetingId: meeting.id,
                  priority: task.priority || 'media',
                  status: task.status || (task.completed ? 'completada' : 'pendiente'),
                  cell: task.cell || client.cell || 'C1GA'
                };
                
                // FILTRAR POR CÉLULAS DEL USUARIO ESTÁNDAR
                if (currentUser?.role === 'standard' && currentUser?.cells && currentUser.cells.length > 0) {
                  // Solo incluir tareas que estén en las células permitidas del usuario actual
                  const taskCell = taskWithCell.cell;
                  if (currentUser.cells.includes(taskCell)) {
                    filteredTasks.push(taskWithCell);
                    console.log(`✅ Tarea incluida en historial de usuario: ${task.id} (célula: ${taskCell})`);
                  } else {
                    console.log(`❌ Tarea filtrada en historial de usuario: ${task.id} (célula: ${taskCell}, células permitidas: ${currentUser.cells.join(', ')})`);
                  }
                } else {
                  // Administradores ven todas las tareas
                  filteredTasks.push(taskWithCell);
                }
              }
            });
          }
        });
      }
    });

    // Aplicar filtros de fecha
    if (userHistoryFilters.dateFrom) {
      const fromDate = new Date(userHistoryFilters.dateFrom);
      filteredTasks = filteredTasks.filter(task => {
        const taskDate = task.createdAt ? new Date(task.createdAt) : new Date(task.meetingDate);
        return taskDate >= fromDate;
      });
    }

    if (userHistoryFilters.dateTo) {
      const toDate = new Date(userHistoryFilters.dateTo);
      toDate.setHours(23, 59, 59, 999); // Incluir todo el día
      filteredTasks = filteredTasks.filter(task => {
        const taskDate = task.createdAt ? new Date(task.createdAt) : new Date(task.meetingDate);
        return taskDate <= toDate;
      });
    }

    // Aplicar filtro de estado
    if (userHistoryFilters.status !== 'all') {
      if (userHistoryFilters.status === 'pending') {
        filteredTasks = filteredTasks.filter(task => !task.completed && task.status !== 'completada');
      } else if (userHistoryFilters.status === 'completed') {
        filteredTasks = filteredTasks.filter(task => task.completed || task.status === 'completada');
      } else if (userHistoryFilters.status === 'approved') {
        filteredTasks = filteredTasks.filter(task => task.status === 'aprobada');
      }
    }

    // Ordenar por fecha de creación (más recientes primero)
    filteredTasks.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(a.meetingDate);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(b.meetingDate);
      return dateB - dateA;
    });

    setUserHistoryTasks(filteredTasks);
  };

  // Función para filtrar datos por cliente
  const filterClientData = async () => {
    if (!clientHistoryFilters.selectedClient) {
      setClientHistoryData({ tasks: [], monthlyStats: {}, files: [] });
      return;
    }

    try {
      // Buscar el cliente seleccionado
      const selectedClient = allClients.find(client => client.name === clientHistoryFilters.selectedClient);
      if (!selectedClient) {
        console.log('Cliente no encontrado:', clientHistoryFilters.selectedClient);
        setClientHistoryData({ tasks: [], monthlyStats: {}, files: [] });
        return;
      }

      console.log('Cargando historial para cliente:', selectedClient.name, 'ID:', selectedClient.id);

      // Cargar tareas del servidor filtradas por cliente
      const tasksResponse = await apiFetch(`/tasks?clientId=${selectedClient.id}`);
      let clientTasks = [];
      
      if (tasksResponse.ok) {
        const serverTasks = await tasksResponse.json();
        console.log('Tareas del servidor para cliente:', serverTasks);
        
      // Procesar tareas del servidor
      clientTasks = serverTasks.map(task => ({
        ...task,
        clientId: selectedClient.id,
        clientName: selectedClient.name,
        meetingDate: task.createdAt || new Date().toISOString(),
        meetingType: 'Tarea del sistema',
        meetingId: task.meetingId || task.id,
        priority: task.priority || 'media',
        status: task.completed ? 'completada' : 'pendiente',
        cell: task.cell || 'C1GA'
      }));

      // FILTRAR TAREAS POR CÉLULAS DEL USUARIO ESTÁNDAR
      if (currentUser?.role === 'standard' && currentUser?.cells && currentUser.cells.length > 0) {
        console.log('🔒 Aplicando filtro de células en historial del cliente...');
        console.log('🏢 Células permitidas para el usuario:', currentUser.cells);
        
        clientTasks = clientTasks.filter(task => {
          // Verificar si la célula de la tarea está permitida
          const taskCell = task.cell || 'Sin célula';
          const cellAllowed = currentUser.cells.includes(taskCell);
          
          if (!cellAllowed) {
            console.log(`❌ Tarea filtrada en historial del cliente:`, {
              taskId: task.id,
              taskCell: taskCell,
              userCells: currentUser.cells
            });
          } else {
            console.log(`✅ Tarea permitida en historial del cliente:`, {
              taskId: task.id,
              taskCell: taskCell
            });
          }
          
          return cellAllowed;
        });
        
        console.log('✅ Tareas del historial después del filtro por células:', clientTasks.length);
      } else {
        console.log('👑 Usuario administrador - sin filtro de células en historial');
      }
      } else {
        console.error('Error al cargar tareas del servidor para el cliente');
        // Fallback: buscar en datos locales
        allClients.forEach(client => {
          if (client.name === clientHistoryFilters.selectedClient) {
            if (client.meetings && Array.isArray(client.meetings)) {
              client.meetings.forEach(meeting => {
                if (meeting.tasks && Array.isArray(meeting.tasks)) {
                  meeting.tasks.forEach(task => {
                    clientTasks.push({
                      ...task,
                      clientId: client.id,
                      clientName: client.name,
                      meetingDate: meeting.date,
                      meetingType: meeting.meetingType,
                      meetingId: meeting.id,
                      priority: task.priority || 'media',
                      status: task.status || (task.completed ? 'completada' : 'pendiente'),
                      cell: task.cell || client.cell || 'C1GA'
                    });
                  });
                }
              });
            }
          }
        });
      }

      // Generar estadísticas mensuales
      const monthlyStats = {};
      clientTasks.forEach(task => {
        if (task.completed || task.status === 'completada') {
          const completedDate = task.completedAt ? new Date(task.completedAt) : new Date(task.createdAt || task.meetingDate);
          const monthKey = `${completedDate.getFullYear()}-${String(completedDate.getMonth() + 1).padStart(2, '0')}`;
          
          if (!monthlyStats[monthKey]) {
            monthlyStats[monthKey] = 0;
          }
          monthlyStats[monthKey]++;
        }
      });

      // Ordenar tareas por fecha (más antiguas primero)
      clientTasks.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(a.meetingDate);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(b.meetingDate);
        return dateA - dateB; // Más antiguas primero
      });

      console.log('Tareas procesadas para historial:', clientTasks);
      console.log('Estadísticas mensuales:', monthlyStats);

      setClientHistoryData({
        tasks: clientTasks,
        monthlyStats,
        files: [] // Los archivos se cargan por separado en el useEffect
      });

    } catch (error) {
      console.error('Error al cargar datos del cliente:', error);
      setClientHistoryData({ tasks: [], monthlyStats: {}, files: [] });
    }
  };

  // Efectos para actualizar los historiales cuando cambian los filtros
  useEffect(() => {
    if (showUserHistoryModal) {
      filterUserTasks();
    }
  }, [userHistoryFilters, allClients, showUserHistoryModal]);

  useEffect(() => {
    if (showClientHistoryModal) {
      filterClientData();
    }
  }, [clientHistoryFilters, allClients, showClientHistoryModal]);

  // Efecto para cerrar dropdowns cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Cerrar dropdown de usuarios si se hace clic fuera
      if (showUserDropdown && !event.target.closest('.user-search-container')) {
        setShowUserDropdown(false);
      }
      
      // Cerrar dropdown de clientes si se hace clic fuera
      if (showClientDropdown && !event.target.closest('.client-search-container')) {
        setShowClientDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown, showClientDropdown]);

  // Cargar archivos del servidor cuando se selecciona un cliente
  useEffect(() => {
    const loadClientFiles = async () => {
      if (clientHistoryFilters.selectedClient) {
        try {
          const response = await apiFetch(`/files/${encodeURIComponent(clientHistoryFilters.selectedClient)}`);
          if (response.ok) {
            const serverFiles = await response.json();
            
            // Convertir archivos del servidor al formato esperado
            const formattedFiles = serverFiles.map(file => ({
              id: Date.now() + Math.random(), // ID temporal
              name: file.name,
              originalName: file.name,
              size: file.size,
              type: 'application/octet-stream', // Tipo genérico
              uploadedAt: file.uploadedAt,
              uploadedBy: 'Usuario',
              clientName: clientHistoryFilters.selectedClient,
              folderPath: `uploads/clients/${clientHistoryFilters.selectedClient.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').substring(0, 50)}`,
              url: file.url
            }));

            // Actualizar solo los archivos en clientHistoryData
            setClientHistoryData(prev => ({
              ...prev,
              files: formattedFiles
            }));
          }
        } catch (error) {
          console.error('Error loading client files:', error);
        }
      }
    };

    if (showClientHistoryModal && clientHistoryFilters.selectedClient) {
      loadClientFiles();
    }
  }, [clientHistoryFilters.selectedClient, showClientHistoryModal]);

  // Función para manejar la selección de archivo
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!fileName) {
        // Si no hay nombre personalizado, usar el nombre del archivo
        setFileName(file.name);
      }
    }
  };

  // Función para crear carpeta del cliente si no existe
  const createClientFolder = (clientName) => {
    // Normalizar nombre del cliente para usar como nombre de carpeta
    const folderName = clientName
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .substring(0, 50); // Limitar longitud
    
    return `uploads/clients/${folderName}`;
  };

  // Función para subir archivo
  const handleFileUpload = async () => {
    if (!selectedFile || !clientHistoryFilters.selectedClient) {
      alert('Por favor selecciona un archivo y un cliente');
      return;
    }

    setUploadingFile(true);
    
    try {
      // Crear FormData para enviar el archivo
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('clientName', clientHistoryFilters.selectedClient);
      formData.append('fileName', fileName || selectedFile.name);
      formData.append('uploadedBy', currentUser?.name || 'Usuario');
      
      const clientData = allClients.find(c => c.name === clientHistoryFilters.selectedClient);
      if (clientData) {
        formData.append('clientId', clientData.id);
      }

      // Subir archivo al servidor
      const response = await apiFetch('/upload', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Error al subir el archivo');
      }

      const newFile = await response.json();
      console.log('Archivo subido exitosamente:', newFile);
      
      // Actualizar los datos del historial del cliente
      setClientHistoryData(prev => ({
        ...prev,
        files: [...(prev.files || []), newFile]
      }));

      // Limpiar el formulario
      setSelectedFile(null);
      setFileName('');
      
      alert(`Archivo subido exitosamente en la carpeta: ${newFile.folderPath}`);
      
    } catch (error) {
      console.error('Error uploading file:', error);
      alert('Error al subir el archivo: ' + error.message);
    } finally {
      setUploadingFile(false);
    }
  };

  // Función para eliminar archivo
  const handleFileDelete = async (fileId) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este archivo?')) {
      return;
    }

    try {
      // Encontrar el archivo para obtener su información
      const fileToDelete = clientHistoryData.files.find(file => file.id === fileId);
      
      if (!fileToDelete) {
        alert('Archivo no encontrado');
        return;
      }

      // Eliminar archivo del servidor
      const response = await apiFetch(`/files/${encodeURIComponent(fileToDelete.clientName)}/${encodeURIComponent(fileToDelete.name)}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Error al eliminar el archivo del servidor');
      }

      // Actualizar los datos del historial del cliente
      setClientHistoryData(prev => ({
        ...prev,
        files: (prev.files || []).filter(file => file.id !== fileId)
      }));

      alert(`Archivo eliminado exitosamente de la carpeta: ${fileToDelete.folderPath}`);
      
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error al eliminar el archivo: ' + error.message);
    }
  };

  // Función para descargar archivo (simulada)
  const handleFileDownload = (file) => {
    // En una implementación real, aquí se descargaría el archivo del servidor
    alert(`Descargando archivo: ${file.name}`);
  };

  // Función para formatear el tamaño del archivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Funciones para grabación de audio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        setAudioBlob(blob);
        setNewTask({...newTask, audioExplanation: `Audio grabado - ${new Date().toLocaleTimeString()}`});
        
        // Detener todas las pistas del stream
        stream.getTracks().forEach(track => track.stop());
      };

      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error al acceder al micrófono:', error);
      alert('Error al acceder al micrófono. Por favor, asegúrate de dar permisos de micrófono al navegador.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const handleAudioRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const playAudio = () => {
    if (audioBlob) {
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audio.play();
    }
  };

  const deleteAudio = () => {
    setAudioBlob(null);
    setNewTask({...newTask, audioExplanation: ''});
  };

  // Función simplificada para transcripción temporal (sin guardar en BD)
  const transcribeAudio = async (task) => {
    if (!task.audioFile) {
      alert('Esta tarea no tiene archivo de audio');
      return;
    }

    setIsTranscribing(true);
    setSelectedTaskForTranscription(task);

    try {
      // Verificar si el navegador soporta Web Speech API
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        // Mostrar "no se transcripción" cuando no hay soporte
        setTranscriptionResults(prev => ({
          ...prev,
          [task.id]: {
            final: 'No se pudo transcribir el audio. Tu navegador no soporta reconocimiento de voz.',
            interim: '',
            isComplete: true,
            isManual: true
          }
        }));
        setIsTranscribing(false);
        return;
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();

      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'es-ES'; // Español
      recognition.maxAlternatives = 1;

      let finalTranscript = '';
      let recognitionTimeout;

      // Mostrar estado inicial de transcribiendo
      setTranscriptionResults(prev => ({
        ...prev,
        [task.id]: {
          final: '',
          interim: 'Iniciando transcripción...',
          isComplete: false,
          isManual: false
        }
      }));

      recognition.onstart = () => {
        console.log('🎤 Reconocimiento de voz iniciado');
        
        // Actualizar estado a "escuchando"
        setTranscriptionResults(prev => ({
          ...prev,
          [task.id]: {
            final: '',
            interim: 'Escuchando audio... Habla ahora.',
            isComplete: false,
            isManual: false
          }
        }));
        
        // Timeout de seguridad para evitar que se quede colgado
        recognitionTimeout = setTimeout(() => {
          console.log('⏰ Timeout de reconocimiento alcanzado');
          recognition.stop();
        }, 30000); // 30 segundos máximo
      };

      recognition.onresult = (event) => {
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        // Actualizar el estado con la transcripción temporal
        setTranscriptionResults(prev => ({
          ...prev,
          [task.id]: {
            final: finalTranscript.trim(),
            interim: interimTranscript,
            isComplete: false,
            isManual: false
          }
        }));
      };

      recognition.onerror = (event) => {
        console.error('Error en reconocimiento de voz:', event.error);
        clearTimeout(recognitionTimeout);
        setIsTranscribing(false);
        
        let errorMessage = 'No se pudo transcribir el audio.';
        if (event.error === 'no-speech') {
          errorMessage = 'No se detectó voz. Asegúrate de que el micrófono esté funcionando.';
        } else if (event.error === 'network') {
          errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
        } else if (event.error === 'not-allowed') {
          errorMessage = 'Permisos de micrófono denegados. Permite el acceso al micrófono.';
        }
        
        setTranscriptionResults(prev => ({
          ...prev,
          [task.id]: {
            final: errorMessage,
            interim: '',
            isComplete: true,
            isManual: true
          }
        }));
      };

      recognition.onend = () => {
        console.log('🏁 Reconocimiento de voz finalizado');
        clearTimeout(recognitionTimeout);
        setIsTranscribing(false);
        
        // Finalizar transcripción
        setTranscriptionResults(prev => ({
          ...prev,
          [task.id]: {
            final: finalTranscript.trim() || 'No se pudo transcribir el audio. Intenta de nuevo.',
            interim: '',
            isComplete: true,
            isManual: finalTranscript.trim() === ''
          }
        }));
      };

      // Verificar conectividad antes de iniciar
      if (!navigator.onLine) {
        setTranscriptionResults(prev => ({
          ...prev,
          [task.id]: {
            final: 'No hay conexión a internet. La transcripción requiere conexión.',
            interim: '',
            isComplete: true,
            isManual: true
          }
        }));
        setIsTranscribing(false);
        return;
      }

      // Iniciar reconocimiento directamente
      console.log('🎤 Iniciando reconocimiento de voz...');
      recognition.start();
        
    } catch (error) {
      console.error('Error en transcripción:', error);
      setIsTranscribing(false);
      
      // Mostrar mensaje de error más descriptivo
      setTranscriptionResults(prev => ({
        ...prev,
        [task.id]: {
          final: 'Error inesperado durante la transcripción. Intenta de nuevo.',
          interim: '',
          isComplete: true,
          isManual: true
        }
      }));
    }
  };

  // Función para limpiar transcripción temporal
  const clearTranscription = (taskId) => {
    setTranscriptionResults(prev => {
      const newResults = { ...prev };
      delete newResults[taskId];
      return newResults;
    });
  };

  // Funciones para comentarios de tareas
  const loadTaskComments = async (taskId) => {
    if (loadingComments[taskId]) return;
    
    setLoadingComments(prev => ({ ...prev, [taskId]: true }));
    
    try {
      const response = await apiFetch(`/tasks/${taskId}/comments`);
      if (response.ok) {
        const comments = await response.json();
        setTaskComments(prev => ({ ...prev, [taskId]: comments }));
      } else {
        console.error('Error al cargar comentarios');
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoadingComments(prev => ({ ...prev, [taskId]: false }));
    }
  };

  const submitComment = async (taskId) => {
    const comment = newComment[taskId];
    const file = commentFiles[taskId];
    
    if ((!comment || !comment.trim()) && !file) {
      alert('Por favor escribe un comentario o selecciona un archivo');
      return;
    }
    
    setSubmittingComment(prev => ({ ...prev, [taskId]: true }));
    
    try {
      const formData = new FormData();
      
      // Obtener el nombre real del usuario actual
      let realUserName = 'Usuario Anónimo';
      if (currentUser?.firstName && currentUser?.lastName) {
        realUserName = `${currentUser.firstName} ${currentUser.lastName}`;
      } else if (currentUser?.firstName) {
        realUserName = currentUser.firstName;
      } else if (currentUser?.name) {
        realUserName = currentUser.name;
      }
      
      console.log('🔍 Enviando comentario con usuario:', realUserName);
      console.log('📝 currentUser completo:', currentUser);
      
      formData.append('userId', currentUser?.id || 'user-1');
      formData.append('userName', realUserName);
      formData.append('comment', comment?.trim() || '');
      
      // Agregar archivo si existe
      if (file) {
        formData.append('file', file);
        formData.append('fileName', commentFileNames[taskId] || file.name);
      }
      
      const response = await apiFetch(`/tasks/${taskId}/comments`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const newCommentData = await response.json();
        console.log('✅ Comentario creado:', newCommentData);
        
        // Actualizar inmediatamente los comentarios en el estado
        setTaskComments(prev => ({
          ...prev,
          [taskId]: [...(prev[taskId] || []), newCommentData]
        }));
        
        // Limpiar el formulario
        setNewComment(prev => ({ ...prev, [taskId]: '' }));
        setCommentFiles(prev => ({ ...prev, [taskId]: null }));
        setCommentFileNames(prev => ({ ...prev, [taskId]: '' }));
        
        // Recargar comentarios desde el servidor para asegurar sincronización
        await loadTaskComments(taskId);
        
      } else {
        console.error('Error al crear comentario');
        alert('Error al crear el comentario');
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Error al enviar el comentario');
    } finally {
      setSubmittingComment(prev => ({ ...prev, [taskId]: false }));
    }
  };

  // Función para manejar selección de archivos en comentarios
  const handleCommentFileSelect = (taskId, file) => {
    setCommentFiles(prev => ({ ...prev, [taskId]: file }));
    setCommentFileNames(prev => ({ ...prev, [taskId]: file.name }));
  };

  // Función para eliminar archivo seleccionado en comentarios
  const removeCommentFile = (taskId) => {
    setCommentFiles(prev => ({ ...prev, [taskId]: null }));
    setCommentFileNames(prev => ({ ...prev, [taskId]: '' }));
  };

  // Función para convertir enlaces en texto clickeable y respetar saltos de línea
  const renderTextWithLinks = (text) => {
    if (!text) return null;
    
    // Primero dividir por saltos de línea
    const lines = text.split('\n');
    
    return lines.map((line, lineIndex) => {
      if (!line.trim()) {
        // Línea vacía - agregar espacio
        return <br key={lineIndex} />;
      }
      
      // Regex para detectar URLs en cada línea
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = line.split(urlRegex);
      
      const lineContent = parts.map((part, partIndex) => {
        if (urlRegex.test(part)) {
          return (
            <a 
              key={partIndex}
              href={part} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-600 hover:underline break-all"
            >
              {part}
            </a>
          );
        }
        return <span key={partIndex}>{part}</span>;
      });
      
      // Retornar la línea con un salto de línea al final (excepto la última)
      return (
        <div key={lineIndex} className="mb-1">
          {lineContent}
        </div>
      );
    });
  };

  const renderTaskCard = (task, index) => (
    <div 
      key={task.id || index}
      className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
              {getPriorityEmoji(task.priority)} {task.priority === 'alta' ? 'Alta' : task.priority === 'media' ? 'Media' : task.priority === 'baja' ? 'Baja' : task.priority === 'urgente' ? 'Urgente' : 'Media'}
            </span>
            {(() => {
              const realStatus = getTaskRealStatus(task);
              return (
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRealStatusColor(realStatus.type)}`}>
                  {realStatus.text}
                </span>
              );
            })()}
          </div>
          <h3 className={`font-medium text-gray-900 mb-2 ${task.completed ? 'line-through opacity-60' : ''}`}>
            {task.description}
          </h3>
          <div className="text-sm text-gray-600 space-y-1">
            <p><span className="font-medium">Cliente:</span> {task.clientName}</p>
            <p><span className="font-medium">Asignado a:</span> {task.assignedTo || 'Sin asignar'}</p>
            <p><span className="font-medium">Fecha límite:</span> {task.dueDate ? formatDateOnly(task.dueDate) : 'Sin fecha'}</p>
            <p><span className="font-medium">Célula:</span> {task.cell || 'Sin asignar'}</p>
            <p><span className="font-medium">Plataforma:</span> {task.platform || 'Sin especificar'}</p>
            <p><span className="font-medium">Área:</span> {task.area || 'Sin especificar'}</p>
            <p><span className="font-medium">Origen:</span> Junta del {formatDateOnly(task.meetingDate)}</p>
            {task.audioExplanation && (
              <p><span className="font-medium">Audio:</span> {task.audioExplanation}</p>
            )}
            {task.evidenceType && (
              <p><span className="font-medium">Evidencia:</span> {task.evidenceType === 'image' ? '🖼️ Imagen' : task.evidenceType === 'link' ? '🔗 Enlace' : task.evidenceType}</p>
            )}
          </div>
        </div>
        <div className="ml-4">
          <input
            type="checkbox"
            checked={task.completed || false}
            onChange={(e) => handleTaskCompletion(task.id, task.meetingId, task.clientId, e.target.checked, task.completionComment || '')}
            className="h-5 w-5 text-green-600 rounded focus:ring-green-500"
          />
        </div>
      </div>
      
      {/* Sección de archivos y evidencias */}
      {(task.evidenceFile || task.audioFile || task.evidenceLink) && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <h4 className="text-sm font-medium text-blue-800 mb-2">📎 Archivos y Evidencias</h4>
          <div className="space-y-2">
            {task.evidenceFile && (
              <div className="flex items-center space-x-2 text-sm">
                <span>🖼️</span>
                <span className="font-medium">Imagen:</span>
                <span className="text-blue-700">{task.evidenceFileName}</span>
                <span className="text-gray-500">({Math.round(task.evidenceFileSize / 1024)} KB)</span>
              </div>
            )}
            {task.audioFile && (
              <div className="flex items-center space-x-2 text-sm">
                <span>🎵</span>
                <span className="font-medium">Audio:</span>
                <span className="text-blue-700">{task.audioFileName}</span>
                <span className="text-gray-500">({Math.round(task.audioFileSize / 1024)} KB)</span>
              </div>
            )}
            {task.evidenceLink && (
              <div className="flex items-center space-x-2 text-sm">
                <span>🔗</span>
                <span className="font-medium">Enlace:</span>
                <a href={task.evidenceLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  Ver evidencia
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {task.completed && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-md">
          <label className="block text-sm font-medium text-green-800 mb-1">Comentario de finalización:</label>
          <textarea
            value={task.completionComment || ''}
            onChange={(e) => handleTaskCompletion(task.id, task.meetingId, task.clientId, task.completed, e.target.value)}
            rows="2"
            className="w-full text-sm border border-green-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
            placeholder="Describe cómo se completó la tarea..."
          />
        </div>
      )}
    </div>
  );

  const renderTaskTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white border border-gray-200 rounded-lg">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarea</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asignado</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prioridad</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Límite</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completar</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {tasks.map((task, index) => (
            <tr key={task.id || index} className={task.completed ? 'opacity-60' : ''}>
              <td className="px-4 py-3">
                <div className={`font-medium text-gray-900 ${task.completed ? 'line-through' : ''}`}>
                  {task.description}
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">{task.clientName}</td>
              <td className="px-4 py-3 text-sm text-gray-600">{task.assignedTo || 'Sin asignar'}</td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                  {getPriorityEmoji(task.priority)} {task.priority?.charAt(0).toUpperCase() + task.priority?.slice(1)}
                </span>
              </td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(task.status, task.completed)}`}>
                  {getStatusText(task.status, task.completed)}
                </span>
              </td>
              <td className="px-4 py-3 text-sm text-gray-600">
                {task.dueDate ? formatDateOnly(task.dueDate) : 'Sin fecha'}
              </td>
              <td className="px-4 py-3">
                <input
                  type="checkbox"
                  checked={task.completed || false}
                  onChange={(e) => handleTaskCompletion(task.id, task.meetingId, task.clientId, e.target.checked, task.completionComment || '')}
                  className="h-4 w-4 text-green-600 rounded focus:ring-green-500"
                />
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      setSelectedTaskForDetails(task);
                      setShowTaskDetailsModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-300 rounded-md"
                  >
                    👁️ Ver Completa
                  </button>
                  
                  {/* Checkbox de Aprobado - Solo visible para administradores en tabla */}
                  {currentUser?.role === 'admin' && (task.completed || task.status === 'completada') && (
                    <label className="flex items-center space-x-1">
                      <input
                        type="checkbox"
                        checked={Boolean(task.approved) || task.status === 'aprobada'}
                        onChange={(e) => handleTaskApproval(task.id, task.meetingId, task.clientId, e.target.checked)}
                        className="h-3 w-3 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-xs text-purple-700">Aprobar</span>
                    </label>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Efecto para cargar comentarios automáticamente cuando cambian las tareas
  useEffect(() => {
    tasks.forEach(task => {
      if (!taskComments[task.id] && !loadingComments[task.id]) {
        loadTaskComments(task.id);
      }
    });
  }, [tasks, taskComments, loadingComments]);

  // Efecto para cargar notificaciones cuando cambian las tareas o comentarios
  useEffect(() => {
    if (tasks.length > 0 && currentUser) {
      loadNotifications();
    }
  }, [tasks, taskComments, currentUser, lastCheckTime]);

  // Efecto para cargar notificaciones periódicamente (cada 30 segundos)
  useEffect(() => {
    if (!currentUser) return;

    const interval = setInterval(() => {
      loadNotifications();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [currentUser, tasks, taskComments]);

  const renderTimelineView = () => {
    // Generar resumen de tareas por fecha
    const tasksByDate = {};
    const tasksByCell = {};
    const tasksByExecutive = {};

    tasks.forEach(task => {
      const date = task.dueDate ? formatDateOnly(task.dueDate) : 'Sin fecha';
      if (!tasksByDate[date]) tasksByDate[date] = 0;
      tasksByDate[date]++;

      // Simular células
      const cell = task.cell || 'C16A';
      if (!tasksByCell[cell]) tasksByCell[cell] = 0;
      tasksByCell[cell]++;

      // Ejecutivos
      const executive = task.assignedTo || 'Sin asignar';
      if (!tasksByExecutive[executive]) tasksByExecutive[executive] = 0;
      tasksByExecutive[executive]++;
    });

    return (
      <div className="space-y-6">
        {/* Título y Resumen */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Tareas Nuevas ({Object.keys(tasksByDate).join(', ')})
          </h3>
          
          <div className="space-y-2 text-sm text-gray-700">
            <div>
              <span className="font-medium">Resumen de Tareas</span>
            </div>
            <div>
              <span className="font-medium">Total de tareas:</span> {tasks.length}
            </div>
            <div>
              <span className="font-medium">Por Célula:</span> {Object.entries(tasksByCell).map(([cell, count]) => `${cell}: ${count}`).join(', ')}
            </div>
            <div>
              <span className="font-medium">Por Ejecutivo:</span> {Object.entries(tasksByExecutive).map(([exec, count]) => `${exec}: ${count}`).join(', ')}
            </div>
          </div>
        </div>

        {/* Lista de Tareas en Timeline */}
        <div className="space-y-6">
          {tasks.map((task, index) => (
            <div key={task.id || index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              {/* Número de tarea */}
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center font-bold text-sm">
                    {index + 1}.
                  </div>
                </div>
                
                <div className="flex-1">
                  {/* Información del Cliente y Tarea */}
                  <div className="mb-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">{task.clientName}</h4>
                    
                    {/* Tarjeta del Título */}
                    <div className="bg-gradient-to-r from-blue-100 to-blue-200 border border-blue-300 rounded-lg p-4 mb-3 shadow-sm">
                      <h5 className="text-lg font-bold text-blue-900 mb-0">
                        📋 {task.title || task.description}
                      </h5>
                    </div>
                    
                    {/* Tarjeta de la Descripción */}
                    <div className="bg-gradient-to-r from-green-100 to-green-200 border border-green-300 rounded-lg p-4 shadow-sm">
                      <div className="text-sm text-green-900">
                        <div className="flex items-center mb-2">
                          <span className="font-bold text-green-800">📝 Descripción:</span>
                        </div>
                        {/* Mostrar descripción como lista numerada si contiene puntos */}
                        {task.points && task.points.trim() ? (
                          task.points.split('\n').filter(point => point.trim()).map((point, pointIndex) => (
                            <div key={pointIndex} className="ml-4 mb-1 text-green-800">
                              <span className="font-medium">{pointIndex + 1}.</span> {point.trim()}
                            </div>
                          ))
                        ) : task.description && task.description.includes('\n') ? (
                          task.description.split('\n').filter(point => point.trim()).map((point, pointIndex) => (
                            <div key={pointIndex} className="ml-4 mb-1 text-green-800">
                              <span className="font-medium">{pointIndex + 1}.</span> {point.trim()}
                            </div>
                          ))
                        ) : (
                          <div className="ml-4 text-green-800">
                            <span className="font-medium">1.</span> {task.description || 'Sin descripción'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Detalles de la tarea */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                    <div>
                      <span className="font-medium">Fecha Límite:</span> {task.dueDate ? formatDateOnly(task.dueDate) : 'Sin fecha'}
                    </div>
                    <div>
                      <span className="font-medium">Asignado a:</span> {task.assignedTo || 'Sin asignar'}
                    </div>
                    <div>
                      <span className="font-medium">Célula:</span> {task.cell || 'Sin asignar'}
                    </div>
                    <div>
                      <span className="font-medium">Plataforma:</span> {task.platform || 'Sin especificar'}
                    </div>
                    <div>
                      <span className="font-medium">Área:</span> {task.area || 'Sin especificar'}
                    </div>
                    <div>
                      <span className="font-medium">Prioridad:</span> {task.priority || 'Media'}
                    </div>
                  </div>

                  <div className="text-sm text-gray-500 mb-4">
                    <span className="font-medium">Creada:</span> {task.createdAt ? formatDateOnly(task.createdAt) + ', ' + new Date(task.createdAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : 'Sin fecha de creación'}
                  </div>

                  {/* Información adicional de realización */}
                  {(task.completed || task.status === 'completada') && (
                    <div className="text-sm text-gray-600 mb-4 space-y-1">
                      <div>
                        <span className="font-medium">Realizada:</span> {task.completedAt ? formatDateOnly(task.completedAt) + ', ' + new Date(task.completedAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : 'Fecha no disponible'}
                      </div>
                      {/* Información de aprobación */}
                      {task.approved && task.approvedAt && (
                        <div>
                          <span className="font-medium">Aprobada:</span> {new Date(task.approvedAt).toLocaleDateString('es-MX') + ', ' + new Date(task.approvedAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })} por {task.approvedBy || 'Admin'}
                        </div>
                      )}
                      {!task.approved && task.rejectedAt && (
                        <div>
                          <span className="font-medium">Rechazada:</span> {new Date(task.rejectedAt).toLocaleDateString('es-MX') + ', ' + new Date(task.rejectedAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                      <div>
                        <span className="font-medium">Tiempo en Realizar:</span> {calculateCompletionTime(task) || 'No calculado'}
                      </div>
                    </div>
                  )}

                  {/* Sección de evidencias y archivos */}
                  {(task.evidenceFile || task.audioFile || task.evidenceLink || task.evidenceType) && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">📎 Evidencias y Archivos</h4>
                      <div className="space-y-3 text-sm">
                        {task.evidenceType && (
                          <div>
                            <span className="font-medium">Tipo de evidencia:</span> {task.evidenceType === 'image' ? '🖼️ Imagen' : task.evidenceType === 'link' ? '🔗 Enlace' : task.evidenceType}
                          </div>
                        )}
                        
                        {/* Imagen con visualización */}
                        {task.evidenceFile && (
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <span>🖼️</span>
                              <span className="font-medium">Imagen:</span>
                              <span className="text-blue-700">{task.evidenceFileName}</span>
                              <span className="text-gray-500">({Math.round(task.evidenceFileSize / 1024)} KB)</span>
                              <button
                                onClick={() => {
                                  setSelectedImage({
                                    url: getFileUrl(task.evidenceFile),
                                    name: task.evidenceFileName
                                  });
                                  setShowImageModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 border border-blue-300 rounded"
                              >
                                👁️ Ver
                              </button>
                            </div>
                            {/* Miniatura de la imagen */}
                            <div className="mt-2">
                                  <img 
                                    src={getFileUrl(task.evidenceFile)}
                                    alt={task.evidenceFileName}
                                    className="max-w-32 max-h-32 object-cover rounded border cursor-pointer hover:opacity-80"
                                    onClick={() => {
                                      setSelectedImage({
                                        url: getFileUrl(task.evidenceFile),
                                        name: task.evidenceFileName
                                      });
                                      setShowImageModal(true);
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                            </div>
                          </div>
                        )}
                        
                        {/* Audio con reproductor */}
                        {task.audioFile && (
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <span>🎵</span>
                              <span className="font-medium">Audio:</span>
                              <span className="text-blue-700">{task.audioFileName}</span>
                              <span className="text-gray-500">({Math.round(task.audioFileSize / 1024)} KB)</span>
                              <button
                                onClick={() => {
                                  // Reproducir usando la URL correcta del servidor
                                  const audioUrl = getFileUrl(task.audioFile);
                                  const audio = new Audio(audioUrl);
                                  audio.play().catch(err => {
                                    console.error('Error reproduciendo audio:', err);
                                    alert('Error al reproducir el audio');
                                  });
                                }}
                                className="text-green-600 hover:text-green-800 text-xs px-2 py-1 border border-green-300 rounded"
                              >
                                ▶️ Reproducir
                              </button>
                              <button
                                onClick={() => transcribeAudio(task)}
                                disabled={isTranscribing}
                                className={`text-xs px-2 py-1 border rounded ${
                                  isTranscribing 
                                    ? 'text-gray-400 border-gray-300 cursor-not-allowed' 
                                    : 'text-blue-600 hover:text-blue-800 border-blue-300'
                                }`}
                              >
                                {isTranscribing && selectedTaskForTranscription?.id === task.id ? '🔄 Transcribiendo...' : '🎤 Transcribir'}
                              </button>
                              {transcriptionResults[task.id] && (
                                <button
                                  onClick={() => clearTranscription(task.id)}
                                  className="text-red-600 hover:text-red-800 text-xs px-2 py-1 border border-red-300 rounded"
                                >
                                  🗑️ Limpiar
                                </button>
                              )}
                            </div>
                            {/* Reproductor de audio funcional */}
                            <div className="mt-2">
                                <audio 
                                  controls 
                                  className="w-full max-w-md"
                                  preload="metadata"
                                >
                                  <source src={getFileUrl(task.audioFile)} type="audio/wav" />
                                  <source src={getFileUrl(task.audioFile)} type="audio/mpeg" />
                                  Tu navegador no soporta el elemento de audio.
                                </audio>
                            </div>
                            
                          {/* Mostrar transcripción temporal directamente debajo del audio */}
                          {transcriptionResults[task.id] && (
                            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-sm text-blue-700">
                                  {transcriptionResults[task.id].isComplete ? '📝 Transcripción:' : '🔄 Transcribiendo...'}
                                </span>
                                <button
                                  onClick={() => clearTranscription(task.id)}
                                  className="text-red-600 hover:text-red-800 text-xs"
                                >
                                  ✕
                                </button>
                              </div>
                              <div className="text-sm text-blue-800 bg-white p-2 rounded border">
                                {transcriptionResults[task.id].final}
                                {transcriptionResults[task.id].interim && (
                                  <span className="text-blue-500 italic"> {transcriptionResults[task.id].interim}</span>
                                )}
                                {!transcriptionResults[task.id].final && !transcriptionResults[task.id].interim && (
                                  <span className="text-gray-500 italic">Iniciando transcripción...</span>
                                )}
                              </div>
                            </div>
                          )}
                          </div>
                        )}
                        
                        {/* Enlace externo */}
                        {task.evidenceLink && (
                          <div className="flex items-center space-x-2">
                            <span>🔗</span>
                            <span className="font-medium">Enlace:</span>
                            <a href={task.evidenceLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                              Ver evidencia externa
                            </a>
                          </div>
                        )}
                        
                        {/* Explicación de audio */}
                        {task.audioExplanation && (
                          <div className="bg-white p-2 rounded border">
                            <span className="font-medium">Explicación de audio:</span> 
                            <p className="mt-1 text-gray-700">{task.audioExplanation}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Enlaces y acciones */}
                  <div className="flex items-center space-x-4 mb-4">
                    <button 
                      onClick={() => {
                        // Primero verificar si la tarea tiene enlace directo
                        if (task.publicityAccountLink) {
                          window.open(task.publicityAccountLink, '_blank');
                        } else {
                          // Fallback: buscar en el cliente
                          const client = allClients.find(c => c.name === task.clientName);
                          if (client?.googleAdsLink) {
                            window.open(client.googleAdsLink, '_blank');
                          } else {
                            alert('No hay enlace de cuenta publicitaria configurado para esta tarea o cliente');
                          }
                        }
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                    >
                      🔗 Ver Cuenta Publicitaria
                    </button>
                    <button 
                      onClick={() => {
                        // Primero verificar si la tarea tiene enlace directo
                        if (task.websiteLink) {
                          window.open(task.websiteLink, '_blank');
                        } else {
                          // Fallback: buscar en el cliente
                          const client = allClients.find(c => c.name === task.clientName);
                          if (client?.websiteLink || client?.googleMapsLink) {
                            window.open(client.websiteLink || client.googleMapsLink, '_blank');
                          } else {
                            alert('No hay enlace de sitio web configurado para esta tarea o cliente');
                          }
                        }
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                    >
                      🔗 Ver Sitio Web
                    </button>
                  </div>

                  {/* Sección de comentarios */}
                  <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-gray-800">💬 Comentarios de la Tarea</h4>
                      {loadingComments[task.id] && (
                        <span className="text-xs text-gray-500">🔄 Cargando comentarios...</span>
                      )}
                    </div>
                    
                    {/* Lista de comentarios existentes con estructura jerárquica */}
                    <div className="space-y-2 mb-3 max-h-64 overflow-y-auto">
                      {taskComments[task.id] && taskComments[task.id].length > 0 ? (
                        (() => {
                          // Separar comentarios principales y respuestas usando parent_comment_id
                          const mainComments = [];
                          const replies = {};

                          // Primero, ordenar todos los comentarios por fecha de creación
                          const sortedComments = [...taskComments[task.id]].sort((a, b) => {
                            const dateA = new Date(a.createdAt || a.created_at);
                            const dateB = new Date(b.createdAt || b.created_at);
                            return dateA - dateB;
                          });

                          sortedComments.forEach(comment => {
                            // Usar parent_comment_id para determinar si es una respuesta
                            const parentId = comment.parent_comment_id || comment.parentCommentId;

                            if (parentId) {
                              // Es una respuesta - agrupar por parent_comment_id
                              if (!replies[parentId]) replies[parentId] = [];
                              replies[parentId].push(comment);
                            } else {
                              // Es un comentario principal
                              mainComments.push(comment);
                            }
                          });
                          
                          return mainComments.map((comment, index) => (
                            <div key={comment.id || index} className="space-y-2">
                              {/* Comentario principal */}
                              <div className="bg-white p-3 rounded border text-sm">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-gray-900">
                                    👤 {comment.userName || comment.user_name || 'Usuario Anónimo'}
                                  </span>
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs text-gray-500">
                                      {formatDateOnly(comment.createdAt || comment.created_at)} {new Date(comment.createdAt || comment.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                    <button
                                      onClick={() => {
                                        // Inicializar respuesta al comentario
                                        const replyKey = `${task.id}-reply-${comment.id}`;
                                        setNewComment(prev => ({ 
                                          ...prev, 
                                          [replyKey]: `@${comment.userName || comment.user_name || 'Usuario'} ` 
                                        }));
                                      }}
                                      className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 border border-blue-300 rounded"
                                    >
                                      💬 Responder
                                    </button>
                                  </div>
                                </div>
                                
                                {/* Texto del comentario con enlaces clickeables */}
                                {comment.comment && (
                                  <div className="text-gray-700 mb-2">
                                    {renderTextWithLinks(comment.comment)}
                                  </div>
                                )}
                                
                                {/* Archivo adjunto si existe */}
                                {(comment.fileName || comment.file_name) && (
                                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-blue-600">📎</span>
                                      <span className="text-sm font-medium text-blue-800">Archivo adjunto:</span>
                                      <span className="text-sm text-blue-700">{comment.fileName || comment.file_name}</span>
                                      {(comment.filePath || comment.file_path) && (
                                          <a 
                                            href={getFileUrl(comment.filePath || comment.file_path)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                          >
                                            📥 Descargar
                                          </a>
                                      )}
                                    </div>
                                    
                                    {/* Mostrar vista previa si es imagen */}
                                    {(comment.filePath || comment.file_path) && (comment.fileName || comment.file_name) && 
                                     (comment.fileName || comment.file_name).toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                                      <div className="mt-2">
                                          <img 
                                            src={getFileUrl(comment.filePath || comment.file_path)}
                                            alt={comment.fileName || comment.file_name}
                                            className="max-w-32 max-h-32 object-cover rounded border cursor-pointer hover:opacity-80"
                                            onClick={() => {
                                              setSelectedImage({
                                                url: getFileUrl(comment.filePath || comment.file_path),
                                                name: comment.fileName || comment.file_name
                                              });
                                              setShowImageModal(true);
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                      </div>
                                    )}
                                    
                                    {/* Mostrar reproductor si es video */}
                                    {(comment.filePath || comment.file_path) && (comment.fileName || comment.file_name) && 
                                     (comment.fileName || comment.file_name).toLowerCase().match(/\.(mp4|webm|ogg|mov|avi)$/i) && (
                                      <div className="mt-2">
                                        <video 
                                          controls 
                                          className="max-w-64 max-h-32 rounded border"
                                          preload="metadata"
                                        >
                                          <source src={getFileUrl(comment.filePath || comment.file_path)} type="video/mp4" />
                                          Tu navegador no soporta el elemento de video.
                                        </video>
                                      </div>
                                    )}
                                    
                                    {/* Mostrar reproductor si es audio */}
                                    {(comment.filePath || comment.file_path) && (comment.fileName || comment.file_name) && 
                                     (comment.fileName || comment.file_name).toLowerCase().match(/\.(mp3|wav|ogg|m4a)$/i) && (
                                      <div className="mt-2">
                                        <audio 
                                          controls 
                                          className="w-full max-w-md"
                                          preload="metadata"
                                        >
                                          <source src={getFileUrl(comment.filePath || comment.file_path)} type="audio/mpeg" />
                                          <source src={getFileUrl(comment.filePath || comment.file_path)} type="audio/wav" />
                                          Tu navegador no soporta el elemento de audio.
                                        </audio>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Formulario de respuesta si está activo */}
                                {newComment[`${task.id}-reply-${comment.id}`] !== undefined && (
                                  <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded">
                                    <div className="space-y-2">
                                      <textarea
                                        value={newComment[`${task.id}-reply-${comment.id}`] || ''}
                                        onChange={(e) => setNewComment(prev => ({ 
                                          ...prev, 
                                          [`${task.id}-reply-${comment.id}`]: e.target.value 
                                        }))}
                                        rows="2"
                                        className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Escribe tu respuesta..."
                                      />
                                      
                                      {/* Sección para subir archivos en respuestas */}
                                      <div className="flex items-center space-x-2">
                                        <input
                                          type="file"
                                          id={`replyFile-${task.id}-${comment.id}`}
                                          onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                              const replyKey = `${task.id}-reply-${comment.id}`;
                                              setCommentFiles(prev => ({ ...prev, [replyKey]: file }));
                                              setCommentFileNames(prev => ({ ...prev, [replyKey]: file.name }));
                                            }
                                          }}
                                          className="hidden"
                                          accept="image/*,application/pdf,.doc,.docx,.txt"
                                        />
                                        <button
                                          type="button"
                                          onClick={() => document.getElementById(`replyFile-${task.id}-${comment.id}`).click()}
                                          className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                                        >
                                          📎 Adjuntar Archivo
                                        </button>
                                        
                                        {/* Mostrar archivo seleccionado en respuesta */}
                                        {commentFiles[`${task.id}-reply-${comment.id}`] && (
                                          <div className="flex items-center space-x-2 text-xs">
                                            <span className="text-gray-600">
                                              📄 {commentFiles[`${task.id}-reply-${comment.id}`].name}
                                            </span>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const replyKey = `${task.id}-reply-${comment.id}`;
                                                setCommentFiles(prev => ({ ...prev, [replyKey]: null }));
                                                setCommentFileNames(prev => ({ ...prev, [replyKey]: '' }));
                                              }}
                                              className="text-red-600 hover:text-red-800"
                                            >
                                              ✕
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                      
                                      <div className="flex justify-end space-x-2">
                                        <button
                                          onClick={() => {
                                            const replyKey = `${task.id}-reply-${comment.id}`;
                                            setNewComment(prev => {
                                              const newState = { ...prev };
                                              delete newState[replyKey];
                                              return newState;
                                            });
                                            // Limpiar también el archivo de respuesta
                                            setCommentFiles(prev => ({ ...prev, [replyKey]: null }));
                                            setCommentFileNames(prev => ({ ...prev, [replyKey]: '' }));
                                          }}
                                          className="text-xs px-2 py-1 text-gray-600 hover:text-gray-800"
                                        >
                                          Cancelar
                                        </button>
                                        <button
                                          onClick={async () => {
                                            const replyKey = `${task.id}-reply-${comment.id}`;
                                            const replyText = newComment[replyKey];
                                            const replyFile = commentFiles[replyKey];
                                            
                                            if ((!replyText || !replyText.trim()) && !replyFile) {
                                              alert('Por favor escribe una respuesta o selecciona un archivo');
                                              return;
                                            }
                                            
                                            // Enviar la respuesta directamente
                                            setSubmittingComment(prev => ({ ...prev, [task.id]: true }));
                                            
                                            try {
                                              const formData = new FormData();
                                              
                                              // Obtener el nombre real del usuario actual
                                              let realUserName = 'Usuario Anónimo';
                                              if (currentUser?.firstName && currentUser?.lastName) {
                                                realUserName = `${currentUser.firstName} ${currentUser.lastName}`;
                                              } else if (currentUser?.firstName) {
                                                realUserName = currentUser.firstName;
                                              } else if (currentUser?.name) {
                                                realUserName = currentUser.name;
                                              }
                                              
                                              formData.append('userId', currentUser?.id || 'user-1');
                                              formData.append('userName', realUserName);
                                              formData.append('comment', replyText?.trim() || '');
                                              formData.append('parentCommentId', comment.id);
                                              
                                              // Agregar archivo si existe
                                              if (replyFile) {
                                                formData.append('file', replyFile);
                                                formData.append('fileName', commentFileNames[replyKey] || replyFile.name);
                                              }
                                              
                                              const response = await apiFetch(`/tasks/${task.id}/comments`, {
                                                method: 'POST',
                                                body: formData
                                              });
                                              
                                              if (response.ok) {
                                                const newCommentData = await response.json();
                                                
                                                // Actualizar inmediatamente los comentarios en el estado
                                                setTaskComments(prev => ({
                                                  ...prev,
                                                  [task.id]: [...(prev[task.id] || []), newCommentData]
                                                }));
                                                
                                                // Limpiar la respuesta y archivo
                                                setNewComment(prev => {
                                                  const newState = { ...prev };
                                                  delete newState[replyKey];
                                                  return newState;
                                                });
                                                setCommentFiles(prev => ({ ...prev, [replyKey]: null }));
                                                setCommentFileNames(prev => ({ ...prev, [replyKey]: '' }));
                                                
                                                // Recargar comentarios desde el servidor
                                                await loadTaskComments(task.id);
                                                
                                              } else {
                                                console.error('Error al crear comentario de respuesta');
                                                alert('Error al enviar la respuesta');
                                              }
                                            } catch (error) {
                                              console.error('Error submitting reply:', error);
                                              alert('Error al enviar la respuesta');
                                            } finally {
                                              setSubmittingComment(prev => ({ ...prev, [task.id]: false }));
                                            }
                                          }}
                                          disabled={submittingComment[task.id]}
                                          className={`text-xs px-3 py-1 rounded ${
                                            submittingComment[task.id]
                                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                              : 'bg-blue-600 text-white hover:bg-blue-700'
                                          }`}
                                        >
                                          {submittingComment[task.id] ? '📤 Enviando...' : '📤 Enviar Respuesta'}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              {/* Respuestas al comentario principal */}
                              {replies[comment.id] && replies[comment.id].length > 0 && (
                                <div className="ml-6 space-y-2">
                                  {replies[comment.id].map((reply, replyIndex) => (
                                    <div key={reply.id || `reply-${replyIndex}`} className="bg-gray-50 p-3 rounded border-l-4 border-blue-300 text-sm">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-gray-800 flex items-center">
                                          <span className="text-blue-600 mr-1">↳</span>
                                          👤 {reply.userName || reply.user_name || 'Usuario Anónimo'}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {formatDateOnly(reply.createdAt || reply.created_at)} {new Date(reply.createdAt || reply.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                      </div>
                                      
                                      {/* Texto de la respuesta con enlaces clickeables */}
                                      {reply.comment && (
                                        <div className="text-gray-700 mb-2">
                                          {renderTextWithLinks(reply.comment)}
                                        </div>
                                      )}
                                      
                                      {/* Archivo adjunto en respuesta si existe */}
                                      {(reply.fileName || reply.file_name) && (
                                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                          <div className="flex items-center space-x-2">
                                            <span className="text-blue-600">📎</span>
                                            <span className="text-sm font-medium text-blue-800">Archivo adjunto:</span>
                                            <span className="text-sm text-blue-700">{reply.fileName || reply.file_name}</span>
                                            {(reply.filePath || reply.file_path) && (
                                                <a 
                                                  href={getFileUrl(reply.filePath || reply.file_path)}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                                >
                                                  📥 Descargar
                                                </a>
                                            )}
                                          </div>
                                          
                                          {/* Vista previa de imagen en respuesta */}
                                          {(reply.filePath || reply.file_path) && (reply.fileName || reply.file_name) && 
                                           (reply.fileName || reply.file_name).toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                                            <div className="mt-2">
                                                <img 
                                                  src={getFileUrl(reply.filePath || reply.file_path)}
                                                  alt={reply.fileName || reply.file_name}
                                                  className="max-w-24 max-h-24 object-cover rounded border cursor-pointer hover:opacity-80"
                                                  onClick={() => {
                                                    setSelectedImage({
                                                      url: getFileUrl(reply.filePath || reply.file_path),
                                                      name: reply.fileName || reply.file_name
                                                    });
                                                    setShowImageModal(true);
                                                  }}
                                                  onError={(e) => {
                                                    e.target.style.display = 'none';
                                                  }}
                                                />
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ));
                        })()
                      ) : (
                        <div className="text-xs text-gray-500 italic">No hay comentarios aún</div>
                      )}
                    </div>
                    
                    {/* Formulario para nuevo comentario */}
                    <div className="space-y-2">
                      <textarea
                        value={newComment[task.id] || ''}
                        onChange={(e) => setNewComment(prev => ({ ...prev, [task.id]: e.target.value }))}
                        rows="2"
                        className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Escribe un comentario sobre esta tarea..."
                      />
                      
                      {/* Sección para subir archivos */}
                      <div className="flex items-center space-x-2">
                        <input
                          type="file"
                          id={`commentFile-${task.id}`}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleCommentFileSelect(task.id, file);
                            }
                          }}
                          className="hidden"
                          accept="image/*,application/pdf,.doc,.docx,.txt"
                        />
                        <button
                          type="button"
                          onClick={() => document.getElementById(`commentFile-${task.id}`).click()}
                          className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                        >
                          📎 Adjuntar Archivo
                        </button>
                        
                        {/* Mostrar archivo seleccionado */}
                        {commentFiles[task.id] && (
                          <div className="flex items-center space-x-2 text-xs">
                            <span className="text-gray-600">
                              📄 {commentFiles[task.id].name}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeCommentFile(task.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              ✕
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-end">
                        <button
                          onClick={() => submitComment(task.id)}
                          disabled={submittingComment[task.id] || (!newComment[task.id]?.trim() && !commentFiles[task.id])}
                          className={`text-xs px-3 py-1 rounded ${
                            submittingComment[task.id] || (!newComment[task.id]?.trim() && !commentFiles[task.id])
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {submittingComment[task.id] ? '📤 Enviando...' : '📤 Enviar Comentario'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Botones de acción */}
                  <div className="flex items-center space-x-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={task.completed || false}
                        onChange={(e) => handleTaskCompletion(task.id, task.meetingId, task.clientId, e.target.checked, task.completionComment || '')}
                        disabled={task.approved || task.status === 'aprobada'} // Bloquear si está aprobada
                        className={`h-4 w-4 text-green-600 rounded focus:ring-green-500 ${
                          task.approved || task.status === 'aprobada' ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                      />
                      <span className={`text-sm ${
                        task.approved || task.status === 'aprobada' ? 'text-gray-400' : 'text-gray-700'
                      }`}>
                        {task.approved || task.status === 'aprobada' ? 'Realizada (Aprobada)' : 'Realizada'}
                      </span>
                    </label>
                    
                    {/* Checkbox de Aprobado - Solo visible para administradores */}
                    {currentUser?.role === 'admin' && (task.completed || task.status === 'completada') && (
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={Boolean(task.approved) || task.status === 'aprobada'}
                          onChange={(e) => handleTaskApproval(task.id, task.meetingId, task.clientId, e.target.checked)}
                          className="h-4 w-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm text-purple-700">Aprobado</span>
                      </label>
                    )}
                    
                    <button 
                      onClick={() => {
                        const taskText = `Tarea: ${task.description}\nCliente: ${task.clientName}\nAsignado a: ${task.assignedTo}\nFecha límite: ${task.dueDate ? formatDateOnly(task.dueDate) : 'Sin fecha'}`;
                        navigator.clipboard.writeText(taskText).then(() => {
                          alert('Tarea copiada al portapapeles');
                        }).catch(() => {
                          alert('Error al copiar la tarea');
                        });
                      }}
                      className="text-gray-600 hover:text-gray-800 text-sm px-3 py-1 border border-gray-300 rounded-md"
                    >
                      📄 Copiar
                    </button>
                    
                    <button 
                      onClick={() => {
                        console.log('Editando tarea:', task);
                        setEditingTask({
                          ...task,
                          id: task.id,
                          clientId: task.clientId,
                          clientName: task.clientName,
                          meetingId: task.meetingId,
                          title: task.title || task.description,
                          description: task.description || task.title,
                          points: task.points || task.description || '',
                          assignedTo: task.assignedTo || '',
                          dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : '',
                          priority: task.priority || 'media',
                          status: task.status || (task.completed ? 'completada' : 'pendiente'),
                          cell: task.cell || '',
                          platform: task.platform || '',
                          area: task.area || '',
                          publicityAccountLink: task.publicityAccountLink || '',
                          websiteLink: task.websiteLink || '',
                          evidenceType: task.evidenceType || '',
                          audioExplanation: task.audioExplanation || '',
                          completed: task.completed || false,
                          approved: task.approved || false,
                          completionComment: task.completionComment || '',
                          createdAt: task.createdAt,
                          updatedAt: task.updatedAt
                        });
                        setShowEditTaskModal(true);
                      }}
                      className="text-gray-600 hover:text-gray-800 text-sm px-3 py-1 border border-gray-300 rounded-md"
                    >
                      ✏️ Editar
                    </button>
                    
                    {/* Botón Eliminar - Solo visible para admin o quien creó la tarea */}
                    {(currentUser?.role === 'admin' || task.createdBy === `${currentUser?.firstName} ${currentUser?.lastName}` || task.createdBy === currentUser?.name) && (
                      <button 
                        onClick={async () => {
                          if (confirm('¿Estás seguro de que quieres eliminar esta tarea?')) {
                            try {
                              // Eliminar la tarea del servidor
                              const response = await apiFetch(`/tasks/${task.id}`, {
                                method: 'DELETE',
                                headers: {
                                  'Content-Type': 'application/json',
                                }
                              });

                              if (!response.ok) {
                                throw new Error('Error al eliminar la tarea en el servidor');
                              }

                              // FORZAR RECARGA COMPLETA DE DATOS DESDE EL SERVIDOR
                              console.log('🔄 Forzando recarga completa después de eliminar tarea...');
                              await reloadData();
                              
                              alert('Tarea eliminada exitosamente');
                            } catch (error) {
                              console.error('Error deleting task:', error);
                              alert('Error al eliminar la tarea: ' + error.message);
                            }
                          }
                        }}
                        className="text-red-600 hover:text-red-800 text-sm px-3 py-1 border border-red-300 rounded-md"
                      >
                        🗑️ Eliminar
                      </button>
                    )}
                  </div>

                  {/* Estado visual */}
                  <div className="mt-4 flex items-center space-x-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                      {getPriorityEmoji(task.priority)} {task.priority === 'alta' ? 'Alta' : task.priority === 'media' ? 'Media' : task.priority === 'baja' ? 'Baja' : task.priority === 'urgente' ? 'Urgente' : 'Media'}
                    </span>
                    {(() => {
                      const realStatus = getTaskRealStatus(task);
                      return (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRealStatusColor(realStatus.type)}`}>
                          {realStatus.text}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCalendarView = () => {
    // Obtener el primer día del mes y el último día
    const firstDayOfMonth = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), 1);
    const lastDayOfMonth = new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 0);
    const firstDayOfWeek = firstDayOfMonth.getDay(); // 0 = domingo
    
    // Generar días del calendario
    const calendarDays = [];
    const daysInMonth = lastDayOfMonth.getDate();
    
    // Días del mes anterior para completar la primera semana
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const prevDate = new Date(firstDayOfMonth);
      prevDate.setDate(prevDate.getDate() - (i + 1));
      calendarDays.push({ date: prevDate, isCurrentMonth: false });
    }
    
    // Días del mes actual
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day);
      calendarDays.push({ date, isCurrentMonth: true });
    }
    
    // Días del mes siguiente para completar la última semana
    const remainingDays = 42 - calendarDays.length; // 6 semanas * 7 días
    for (let day = 1; day <= remainingDays; day++) {
      const nextDate = new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, day);
      calendarDays.push({ date: nextDate, isCurrentMonth: false });
    }
    
    // Agrupar tareas por fecha
    const tasksByDate = {};
    tasks.forEach(task => {
      const taskDate = task.dueDate ? new Date(task.dueDate) : new Date(task.meetingDate);
      const dateKey = taskDate.toDateString();
      if (!tasksByDate[dateKey]) {
        tasksByDate[dateKey] = [];
      }
      tasksByDate[dateKey].push(task);
    });
    
    const monthNames = [
      'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ];
    
    const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    
    const goToPreviousMonth = () => {
      setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
    };
    
    const goToNextMonth = () => {
      setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1));
    };
    
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header del calendario */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Calendario de Tareas</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Mostrar Menú Células</span>
              <button className="text-gray-400">▼</button>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={goToPreviousMonth} className="p-1 hover:bg-gray-100 rounded">
                ←
              </button>
              <h4 className="text-lg font-medium">
                {monthNames[calendarDate.getMonth()]} de {calendarDate.getFullYear()}
              </h4>
              <button onClick={goToNextMonth} className="p-1 hover:bg-gray-100 rounded">
                →
              </button>
            </div>
            
            <div className="flex items-center space-x-2">
              <div className="flex bg-gray-100 rounded-md">
                <button
                  onClick={() => setCalendarViewType('Mes')}
                  className={`px-3 py-1 text-sm rounded-md ${
                    calendarViewType === 'Mes' ? 'bg-black text-white' : 'text-gray-600'
                  }`}
                >
                  Mes
                </button>
                <button
                  onClick={() => setCalendarViewType('Semana')}
                  className={`px-3 py-1 text-sm rounded-md ${
                    calendarViewType === 'Semana' ? 'bg-black text-white' : 'text-gray-600'
                  }`}
                >
                  Semana
                </button>
                <button
                  onClick={() => setCalendarViewType('Día')}
                  className={`px-3 py-1 text-sm rounded-md ${
                    calendarViewType === 'Día' ? 'bg-black text-white' : 'text-gray-600'
                  }`}
                >
                  Día
                </button>
              </div>
              <input
                type="date"
                className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                value={calendarDate.toISOString().split('T')[0]}
                onChange={(e) => setCalendarDate(new Date(e.target.value))}
              />
            </div>
          </div>
        </div>
        
        {/* Grid del calendario */}
        <div className="p-4">
          {/* Encabezados de días */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="p-2 text-center text-sm font-medium text-gray-600">
                {day}
              </div>
            ))}
          </div>
          
          {/* Días del calendario */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((calendarDay, index) => {
              const dayTasks = tasksByDate[calendarDay.date.toDateString()] || [];
              const isToday = calendarDay.date.toDateString() === new Date().toDateString();
              const isSelected = calendarDay.date.getDate() === 23 && calendarDay.isCurrentMonth; // Día seleccionado en la imagen
              
              return (
                <div
                  key={index}
                  className={`min-h-[100px] p-2 border border-gray-200 ${
                    !calendarDay.isCurrentMonth ? 'bg-gray-50 text-gray-400' : 'bg-white'
                  } ${isSelected ? 'bg-blue-50 border-blue-300' : ''} ${
                    isToday ? 'bg-yellow-50 border-yellow-300' : ''
                  }`}
                >
                  <div className="text-sm font-medium mb-1">
                    {calendarDay.date.getDate()}
                  </div>
                  
                  {/* Tareas del día */}
                  <div className="space-y-1">
                    {dayTasks.slice(0, 3).map((task, taskIndex) => (
                      <div
                        key={taskIndex}
                        className="text-xs p-1 rounded bg-blue-100 text-blue-800 truncate cursor-pointer hover:bg-blue-200"
                        title={`${task.clientName}: ${task.description}`}
                      >
                        {task.clientName}
                      </div>
                    ))}
                    {dayTasks.length > 3 && (
                      <div className="text-xs text-gray-500">
                        +{dayTasks.length - 3} más
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md">
      <div className="p-6">
        {/* Header Principal - Responsivo */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 space-y-4 lg:space-y-0">
          <div>
            <h2 className="text-xl lg:text-2xl font-bold text-[#0061A8]">Mis Tareas</h2>
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:space-x-3">
            {/* Historial Usuarios */}
            <button 
              onClick={() => setShowUserHistoryModal(true)}
              className="flex items-center px-2 lg:px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm lg:text-base"
            >
              <span className="mr-1 lg:mr-2">👥</span>
              <span className="hidden sm:inline">Historial Usuarios</span>
              <span className="sm:hidden">Usuarios</span>
            </button>
            
            {/* Historial Cliente */}
            <button 
              onClick={() => setShowClientHistoryModal(true)}
              className="flex items-center px-2 lg:px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm lg:text-base"
            >
              <span className="mr-1 lg:mr-2">⏰</span>
              <span className="hidden sm:inline">Historial Cliente</span>
              <span className="sm:hidden">Cliente</span>
            </button>
            
            {/* Notificaciones */}
            <button 
              onClick={() => {
                setShowNotificationsModal(true);
                markNotificationsAsRead();
              }}
              className="relative p-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <span className="text-lg lg:text-xl">🔔</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 lg:h-5 lg:w-5 flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            
            {/* Nueva Tarea */}
            <button 
              onClick={() => setShowNewTaskModal(true)}
              className="bg-black text-white px-3 lg:px-4 py-2 rounded-md hover:bg-gray-800 transition-colors flex items-center text-sm lg:text-base"
            >
              <span className="mr-1 lg:mr-2">+</span>
              <span className="hidden sm:inline">Nueva Tarea</span>
              <span className="sm:hidden">Nueva</span>
            </button>
          </div>
        </div>

        {/* Barra de Navegación de Tareas */}
        <div className="mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-6">
              {/* Buscador */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar por cliente o usuario..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-64 pl-4 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {/* Navegación de Estados */}
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setFilterStatus('all')}
                  className={`px-3 py-2 rounded-md transition-colors text-sm ${
                    filterStatus === 'all' 
                      ? 'bg-gray-100 text-gray-800 border border-gray-200' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Todas
                </button>
                <button
                  onClick={() => setFilterStatus('new')}
                  className={`px-3 py-2 rounded-md transition-colors text-sm ${
                    filterStatus === 'new' 
                      ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Tareas Nuevas
                </button>
                <button
                  onClick={() => setFilterStatus('completed')}
                  className={`px-3 py-2 rounded-md transition-colors text-sm ${
                    filterStatus === 'completed' 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Tareas Realizadas
                </button>
                <button
                  onClick={() => setFilterStatus('approved')}
                  className={`px-3 py-2 rounded-md transition-colors text-sm ${
                    filterStatus === 'approved' 
                      ? 'bg-purple-100 text-purple-800 border border-purple-200' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Tareas Aprobadas
                </button>
                <button
                  onClick={() => setShowDashboard(true)}
                  className={`px-3 py-2 rounded-md transition-colors text-sm ${
                    showDashboard 
                      ? 'bg-orange-100 text-orange-800 border border-orange-200' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`px-3 py-2 rounded-md transition-colors text-sm ${
                    viewMode === 'calendar' 
                      ? 'bg-indigo-100 text-indigo-800 border border-indigo-200' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Calendario
                </button>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Filtros */}
              <button 
                onClick={() => setShowFiltersModal(true)}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <span className="mr-2">🔍</span>
                Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Selector de Vista - Estilo CoreUI */}
        <div className="mb-6">
          <div className="flex items-center justify-center">
            <div className="inline-flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('timeline')}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'timeline' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="mr-2">📅</span>
                Timeline
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'cards' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="mr-2">🗂️</span>
                Tarjetas
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'table' 
                    ? 'bg-white text-blue-600 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="mr-2">📊</span>
                Tabla
              </button>
            </div>
          </div>
        </div>

        {/* Barra de Filtros de Células */}
        {showCellsMenu && (
          <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">Filtrar por Células:</span>
              <button 
                onClick={() => setShowCellsMenu(false)}
                className="text-xs text-blue-600 hover:text-blue-800 border border-blue-300 rounded px-2 py-1 flex-shrink-0"
              >
                ← Ocultar Menú Células
              </button>
            </div>
            
            {/* Filtros dinámicos basados en las células del usuario */}
            <div className="space-y-3">
              {(() => {
                // Obtener las células del usuario actual
                let userCells = [];
                
                if (currentUser?.role === 'admin') {
                  // Administradores ven TODAS las células (lista completa)
                  userCells = ['C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA', 'C7GA', 'C8GA', 'C1MA', 'C2MA', 'C3MA', 'C4MA', 'C5MA'];
                } else if (currentUser?.cells && currentUser.cells.length > 0) {
                  // Usuarios estándar solo ven sus células asignadas
                  userCells = currentUser.cells;
                } else {
                  // Fallback: mostrar células por defecto
                  userCells = ['C1GA', 'C2GA', 'C3GA', 'C4GA'];
                }
                
                console.log('🏢 Células a mostrar en filtro:', userCells);
                console.log('👤 Usuario actual:', currentUser?.firstName, currentUser?.lastName, 'Rol:', currentUser?.role);
                
                // Agrupar células en filas de 4 para mejor visualización
                const cellRows = [];
                for (let i = 0; i < userCells.length; i += 4) {
                  cellRows.push(userCells.slice(i, i + 4));
                }
                
                return cellRows.map((cellRow, rowIndex) => (
                  <div key={rowIndex} className="flex flex-wrap gap-2">
                    {cellRow.map(cell => (
                      <div key={cell} className="flex space-x-2">
                        <button
                          onClick={() => {
                            if (selectedCells.includes(`Pendientes ${cell}`)) {
                              setSelectedCells(selectedCells.filter(c => c !== `Pendientes ${cell}`));
                            } else {
                              setSelectedCells([...selectedCells, `Pendientes ${cell}`]);
                            }
                          }}
                          className={`px-3 py-1 text-xs rounded border transition-colors ${
                            selectedCells.includes(`Pendientes ${cell}`)
                              ? 'bg-blue-100 text-blue-800 border-blue-300'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          Pendientes {cell}
                        </button>
                        <button
                          onClick={() => {
                            if (selectedCells.includes(`Realizadas ${cell}`)) {
                              setSelectedCells(selectedCells.filter(c => c !== `Realizadas ${cell}`));
                            } else {
                              setSelectedCells([...selectedCells, `Realizadas ${cell}`]);
                            }
                          }}
                          className={`px-3 py-1 text-xs rounded border transition-colors ${
                            selectedCells.includes(`Realizadas ${cell}`)
                              ? 'bg-green-100 text-green-800 border-green-300'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          Realizadas {cell}
                        </button>
                      </div>
                    ))}
                  </div>
                ));
              })()}
              
              {/* Información adicional para el usuario */}
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>
                    {currentUser?.role === 'admin' 
                      ? `Mostrando todas las células disponibles (${currentUser?.cells?.length || 'todas'})` 
                      : `Mostrando tus células asignadas (${currentUser?.cells?.length || 0})`
                    }
                  </span>
                  {selectedCells.length > 0 && (
                    <button
                      onClick={() => setSelectedCells([])}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Botón para mostrar menú de células cuando está oculto */}
        {!showCellsMenu && (
          <div className="mb-6 flex justify-end">
            <button 
              onClick={() => setShowCellsMenu(true)}
              className="text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded-md px-3 py-1"
            >
              → Mostrar Menú Células
            </button>
          </div>
        )}

        {/* Estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-2xl mr-3">📋</div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Todas</p>
                <p className="text-xl font-bold text-blue-800">{filteredAndSortedTasks.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-2xl mr-3">⏳</div>
              <div>
                <p className="text-sm text-yellow-600 font-medium">Tareas Nuevas</p>
                <p className="text-xl font-bold text-yellow-800">
                  {filteredAndSortedTasks.filter(t => !t.completed && t.status !== 'completada' && t.status !== 'cancelada' && !t.approved).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-2xl mr-3">✅</div>
              <div>
                <p className="text-sm text-green-600 font-medium">Tareas Realizadas</p>
                <p className="text-xl font-bold text-green-800">
                  {filteredAndSortedTasks.filter(t => t.completed || t.status === 'completada').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="text-2xl mr-3">👍</div>
              <div>
                <p className="text-sm text-purple-600 font-medium">Tareas Aprobadas</p>
                <p className="text-xl font-bold text-purple-800">
                  {filteredAndSortedTasks.filter(t => t.approved || t.status === 'aprobada').length}
                </p>
              </div>
            </div>
          </div>
        </div>


        {/* Contenido */}
        {viewMode === 'calendar' ? (
          renderCalendarView()
        ) : filteredAndSortedTasks.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay tareas</h3>
            <p className="text-gray-500">No se encontraron tareas que coincidan con los filtros seleccionados.</p>
          </div>
        ) : (
          <div>
            {viewMode === 'timeline' ? (
              renderTimelineView()
            ) : viewMode === 'cards' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAndSortedTasks.map((task, index) => renderTaskCard(task, index))}
              </div>
            ) : (
              renderTaskTable()
            )}
          </div>
        )}
      </div>

      {/* Modal de Filtros de Tareas */}
      {showFiltersModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Filtros de Tareas</h2>
                <button 
                  onClick={() => setShowFiltersModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Asignado a</label>
                  <select 
                    value={filterAssignedTo}
                    onChange={(e) => setFilterAssignedTo(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">Todos</option>
                    {allUsers.map(user => (
                      <option key={user} value={user}>{user}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prioridad</label>
                  <select 
                    value={filterPriority}
                    onChange={(e) => setFilterPriority(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="all">Todas</option>
                    <option value="urgente">🔴 Urgente</option>
                    <option value="alta">🟠 Alta</option>
                    <option value="media">🟡 Media</option>
                    <option value="baja">🟢 Baja</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                  <select 
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="all">Todas</option>
                    <option value="new">Tareas Nuevas</option>
                    <option value="completed">Tareas Realizadas</option>
                    <option value="approved">Tareas Aprobadas</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Búsqueda</label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar por cliente, usuario o descripción..."
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Células Seleccionadas</label>
                  <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-3">
                    {selectedCells.length === 0 ? (
                      <p className="text-gray-500 text-sm">Ninguna célula seleccionada</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {selectedCells.map(cell => (
                          <span key={cell} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                            {cell}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                <button 
                  onClick={() => {
                    // Los filtros ya se aplican automáticamente por los useEffect
                    setShowFiltersModal(false);
                  }}
                  className="w-full bg-black text-white py-3 px-4 rounded-md hover:bg-gray-800"
                >
                  Aplicar Filtros
                </button>
                <button 
                  onClick={() => {
                    setFilterAssignedTo('');
                    setFilterStatus('all');
                    setFilterPriority('all');
                    setSearchTerm('');
                    setSelectedCells([]);
                  }}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-200"
                >
                  Limpiar Filtros
                </button>
                
                {/* Botones de compartir separados */}
                <div className="grid grid-cols-1 gap-2">
                  <button 
                    onClick={shareFilteredTasks}
                    className="w-full bg-green-500 text-white py-3 px-4 rounded-md hover:bg-green-600 flex items-center justify-center"
                  >
                    <span className="mr-2">📱</span>
                    Compartir Tareas Filtradas por WhatsApp
                  </button>
                  <button 
                    onClick={shareAllTasks}
                    className="w-full bg-blue-500 text-white py-3 px-4 rounded-md hover:bg-blue-600 flex items-center justify-center"
                  >
                    <span className="mr-2">📱</span>
                    Compartir Todas las Tareas por WhatsApp
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Dashboard de Métricas */}
      {showDashboard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Dashboard de Métricas</h2>
                <button 
                  onClick={() => setShowDashboard(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="mb-4 flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Métrica a Mostrar</label>
                  <select className="border border-gray-300 rounded-md px-3 py-2">
                    <option>Tareas Completadas</option>
                    <option>Tareas Pendientes</option>
                    <option>Tiempo Promedio</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Seleccionar Período</label>
                  <select className="border border-gray-300 rounded-md px-3 py-2 w-64">
                    <option>Todo el Tiempo</option>
                    <option>Último Mes</option>
                    <option>Última Semana</option>
                  </select>
                </div>
                <button className="mt-6 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600">
                  Filtrar por Período
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top 10 Métricas por Usuario */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Top 10 Métricas por Usuario</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tareas Nuevas</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tareas Realizadas</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tareas Aprobadas</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total Completadas</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tiempo Invertido</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Promedio por Tarea</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {allUsers.slice(0, 10).map((user, index) => (
                          <tr key={user}>
                            <td className="px-4 py-2 text-sm font-medium text-gray-900">{user}</td>
                            <td className="px-4 py-2 text-sm text-gray-600">
                              {tasks.filter(t => t.assignedTo === user && !t.completed).length}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">
                              {tasks.filter(t => t.assignedTo === user && t.completed).length}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">
                              {tasks.filter(t => t.assignedTo === user && t.status === 'aprobada').length}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">
                              {tasks.filter(t => t.assignedTo === user && t.completed).length}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">
                              {Math.floor(Math.random() * 50) + 10}h {Math.floor(Math.random() * 60)}m
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">
                              {Math.floor(Math.random() * 5) + 1}h {Math.floor(Math.random() * 60)}m
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Métricas por Cliente */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Métricas por Cliente</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border border-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tareas Nuevas</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tareas Realizadas</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tareas Aprobadas</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total Completadas</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tiempo Invertido</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Promedio por Tarea</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {Array.from(new Set(tasks.map(t => t.clientName))).slice(0, 10).map((clientName, index) => (
                          <tr key={clientName}>
                            <td className="px-4 py-2 text-sm font-medium text-gray-900">{clientName}</td>
                            <td className="px-4 py-2 text-sm text-gray-600">
                              {tasks.filter(t => t.clientName === clientName && !t.completed).length}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">
                              {tasks.filter(t => t.clientName === clientName && t.completed).length}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">
                              {tasks.filter(t => t.clientName === clientName && t.status === 'aprobada').length}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">
                              {tasks.filter(t => t.clientName === clientName && t.completed).length}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">
                              {Math.floor(Math.random() * 100) + 20}h {Math.floor(Math.random() * 60)}m
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">
                              {Math.floor(Math.random() * 10) + 2}h {Math.floor(Math.random() * 60)}m
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button className="bg-black text-white px-6 py-2 rounded-md hover:bg-gray-800 flex items-center">
                  <span className="mr-2">+</span>
                  CREAR NUEVA TAREA
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Historial por Usuario */}
      {showUserHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Historial por Usuario</h2>
                <button 
                  onClick={() => setShowUserHistoryModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                {/* Selector de Usuario */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Seleccionar Usuario</label>
                  <select 
                    value={userHistoryFilters.selectedUser}
                    onChange={(e) => setUserHistoryFilters({...userHistoryFilters, selectedUser: e.target.value})}
                    className="w-full border border-blue-500 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecciona un usuario</option>
                    {(() => {
                      // Filtrar usuarios según el rol del usuario actual
                      if (currentUser?.role === 'standard' && currentUser?.cells && currentUser.cells.length > 0) {
                        // Para usuarios estándar, solo mostrar usuarios que tienen tareas en sus células
                        const allowedUsers = allUsers.filter(user => {
                          // Buscar si este usuario tiene tareas en las células permitidas
                          const userTasks = [];
                          allClients.forEach(client => {
                            if (client.meetings && Array.isArray(client.meetings)) {
                              client.meetings.forEach(meeting => {
                                if (meeting.tasks && Array.isArray(meeting.tasks)) {
                                  meeting.tasks.forEach(task => {
                                    if (task.assignedTo === user) {
                                      userTasks.push({
                                        ...task,
                                        cell: task.cell || client.cell || 'C1GA'
                                      });
                                    }
                                  });
                                }
                              });
                            }
                          });
                          
                          // Verificar si alguna tarea del usuario está en las células permitidas
                          return userTasks.some(task => 
                            currentUser.cells.includes(task.cell)
                          );
                        });
                        
                        return allowedUsers.map(user => (
                          <option key={user} value={user}>{user}</option>
                        ));
                      } else {
                        // Para administradores, mostrar todos los usuarios
                        return allUsers.map(user => (
                          <option key={user} value={user}>{user}</option>
                        ));
                      }
                    })()}
                  </select>
                </div>

                {/* Filtros de Fecha */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Inicio</label>
                    <input 
                      type="date" 
                      value={userHistoryFilters.dateFrom}
                      onChange={(e) => setUserHistoryFilters({...userHistoryFilters, dateFrom: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Fin</label>
                    <input 
                      type="date" 
                      value={userHistoryFilters.dateTo}
                      onChange={(e) => setUserHistoryFilters({...userHistoryFilters, dateTo: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>

                {/* Filtro por Estado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filtrar por Estado</label>
                  <select 
                    value={userHistoryFilters.status}
                    onChange={(e) => setUserHistoryFilters({...userHistoryFilters, status: e.target.value})}
                    className="w-full border border-blue-500 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Todas las Tareas</option>
                    <option value="pending">Tareas Pendientes</option>
                    <option value="completed">Tareas Completadas</option>
                    <option value="approved">Tareas Aprobadas</option>
                  </select>
                </div>

                {/* Título de Tareas Asignadas */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {userHistoryFilters.selectedUser 
                      ? `Tareas Asignadas a ${userHistoryFilters.selectedUser}` 
                      : 'Tareas Asignadas'
                    }
                  </h3>
                  
                  {/* Lista de tareas o mensaje cuando no hay */}
                  {userHistoryTasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {userHistoryFilters.selectedUser 
                        ? 'No hay tareas asignadas a este usuario con los filtros actuales.'
                        : 'Selecciona un usuario para ver sus tareas.'
                      }
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {userHistoryTasks.map((task, index) => (
                        <div key={task.id || index} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-gray-900 mb-1">{task.description}</h4>
                              <div className="text-sm text-gray-600 space-y-1">
                                <p><span className="font-medium">Cliente:</span> {task.clientName}</p>
                                <p><span className="font-medium">Estado:</span> {getStatusText(task.status, task.completed)}</p>
                                <p><span className="font-medium">Prioridad:</span> {task.priority}</p>
                                <p><span className="font-medium">Fecha:</span> {task.createdAt ? formatDateOnly(task.createdAt) : formatDateOnly(task.meetingDate)}</p>
                              </div>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(task.status, task.completed)}`}>
                              {task.completed ? '✅' : '⏳'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Historial por Cliente */}
      {showClientHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Historial por Cliente</h2>
                <button 
                  onClick={() => setShowClientHistoryModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Selector de Cliente */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Seleccionar Cliente</label>
                  <select 
                    value={clientHistoryFilters.selectedClient}
                    onChange={(e) => setClientHistoryFilters({...clientHistoryFilters, selectedClient: e.target.value})}
                    className="w-full border border-blue-500 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Selecciona un cliente</option>
                    {Array.from(new Set(
                      allClients
                        .filter(client => {
                          // Filtrar por células del usuario estándar
                          if (currentUser?.role === 'standard' && currentUser?.cells && currentUser.cells.length > 0) {
                            const clientCells = client.cells || [client.cell];
                            const hasAllowedCell = clientCells.some(clientCell => 
                              currentUser.cells.includes(clientCell)
                            );
                            return hasAllowedCell;
                          }
                          // Administradores ven todos los clientes
                          return true;
                        })
                        .map(c => c.name)
                    )).map(clientName => (
                      <option key={clientName} value={clientName}>{clientName}</option>
                    ))}
                  </select>
                </div>

                {/* Gráfico de Tareas Completadas por Mes */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Tareas Completadas por Mes
                    {clientHistoryFilters.selectedClient && ` - ${clientHistoryFilters.selectedClient}`}
                  </h3>
                  <div className="h-64 bg-white rounded border flex items-end justify-center p-4">
                    {/* Gráfico dinámico basado en datos reales */}
                    {Object.keys(clientHistoryData.monthlyStats).length > 0 ? (
                      <div className="flex items-end space-x-2 h-full">
                        {Object.entries(clientHistoryData.monthlyStats).map(([month, count], index) => {
                          const maxCount = Math.max(...Object.values(clientHistoryData.monthlyStats));
                          const height = maxCount > 0 ? (count / maxCount) * 200 : 20;
                          return (
                            <div 
                              key={month}
                              className="bg-blue-500 w-8 rounded-t flex items-end justify-center text-xs text-white font-bold"
                              style={{ height: `${height}px` }}
                              title={`${month}: ${count} tareas`}
                            >
                              {count}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-gray-500 text-center">
                        {clientHistoryFilters.selectedClient 
                          ? 'No hay tareas completadas para mostrar'
                          : 'Selecciona un cliente para ver las estadísticas'
                        }
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>0</span>
                    <span>{clientHistoryData.tasks.filter(t => t.completed || t.status === 'completada').length} tareas</span>
                  </div>
                </div>

                {/* Archivos del Cliente */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Archivos del Cliente</h3>
                  
                  <div className="flex items-center space-x-4 mb-4">
                    <input
                      type="file"
                      id="fileInput"
                      onChange={handleFileSelect}
                      className="hidden"
                      disabled={!clientHistoryFilters.selectedClient}
                    />
                    <button 
                      onClick={() => document.getElementById('fileInput').click()}
                      disabled={!clientHistoryFilters.selectedClient}
                      className={`px-4 py-2 rounded-md border ${
                        clientHistoryFilters.selectedClient 
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                          : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      Seleccionar archivo
                    </button>
                    <span className="text-gray-500">
                      {selectedFile ? selectedFile.name : 'Sin archivos seleccionados'}
                    </span>
                    <input 
                      type="text" 
                      placeholder="Nombre del archivo (opcional)"
                      value={fileName}
                      onChange={(e) => setFileName(e.target.value)}
                      disabled={!selectedFile}
                      className={`flex-1 border border-gray-300 rounded-md px-3 py-2 ${
                        !selectedFile ? 'bg-gray-50 text-gray-400' : ''
                      }`}
                    />
                    <button 
                      onClick={handleFileUpload}
                      disabled={!selectedFile || uploadingFile || !clientHistoryFilters.selectedClient}
                      className={`px-4 py-2 rounded-md ${
                        selectedFile && !uploadingFile && clientHistoryFilters.selectedClient
                          ? 'bg-black text-white hover:bg-gray-800' 
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {uploadingFile ? 'Subiendo...' : 'Subir Archivo'}
                    </button>
                  </div>

                  {/* Lista de archivos */}
                  {clientHistoryData.files.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {clientHistoryFilters.selectedClient 
                        ? `No hay archivos para ${clientHistoryFilters.selectedClient}.`
                        : 'Selecciona un cliente para gestionar archivos.'
                      }
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-900">Archivos subidos:</h4>
                      {clientHistoryData.files.map((file, index) => (
                        <div key={file.id || index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="text-2xl">
                              {file.type?.includes('image') ? '🖼️' : 
                               file.type?.includes('pdf') ? '📄' : 
                               file.type?.includes('word') ? '📝' : 
                               file.type?.includes('excel') ? '📊' : '📁'}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{file.name}</p>
                              <p className="text-sm text-gray-500">
                                {formatFileSize(file.size)} • Subido por {file.uploadedBy} • {formatDateOnly(file.uploadedAt)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleFileDownload(file)}
                              className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-300 rounded-md"
                            >
                              📥 Descargar
                            </button>
                            <button
                              onClick={() => handleFileDelete(file.id)}
                              className="text-red-600 hover:text-red-800 text-sm px-3 py-1 border border-red-300 rounded-md"
                            >
                              🗑️ Eliminar
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Línea de Tiempo de Tareas */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Línea de Tiempo de Tareas
                    {clientHistoryFilters.selectedClient && ` - ${clientHistoryFilters.selectedClient}`}
                  </h3>
                  
                  {clientHistoryData.tasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {clientHistoryFilters.selectedClient 
                        ? `No hay tareas para ${clientHistoryFilters.selectedClient}.`
                        : 'Selecciona un cliente para ver su línea de tiempo de tareas.'
                      }
                    </div>
                  ) : (
                    <div className="space-y-6 max-h-96 overflow-y-auto">
                      {clientHistoryData.tasks.map((task, index) => {
                        const statusColor = task.completed || task.status === 'completada' ? 'bg-green-500' : 
                                          task.status === 'en_progreso' ? 'bg-yellow-500' : 'bg-blue-500';
                        
                        return (
                          <div key={task.id || index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                            {/* Número de tarea y estado */}
                            <div className="flex items-start space-x-4">
                              <div className="flex-shrink-0">
                                <div className={`w-8 h-8 ${statusColor} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                                  {index + 1}
                                </div>
                              </div>
                              
                              <div className="flex-1">
                                {/* Información básica de la tarea */}
                                <div className="mb-4">
                                  <h4 className="text-lg font-semibold text-gray-900 mb-1">{task.title || task.description}</h4>
                                  <p className="text-sm text-gray-600 mb-2">
                                    {task.meetingType || 'Tarea del sistema'}
                                  </p>
                                  
                                  {/* Descripción como lista numerada si contiene puntos */}
                                  <div className="text-sm text-gray-600 space-y-1">
                                    <div>
                                      <span className="font-medium">Descripción:</span>
                                    </div>
                                    {task.points && task.points.trim() ? (
                                      task.points.split('\n').filter(point => point.trim()).map((point, pointIndex) => (
                                        <div key={pointIndex} className="ml-4">
                                          {pointIndex + 1}. {point.trim()}
                                        </div>
                                      ))
                                    ) : task.description && task.description.includes('\n') ? (
                                      task.description.split('\n').filter(point => point.trim()).map((point, pointIndex) => (
                                        <div key={pointIndex} className="ml-4">
                                          {pointIndex + 1}. {point.trim()}
                                        </div>
                                      ))
                                    ) : (
                                      <div className="ml-4">
                                        1. {task.description || 'Sin descripción'}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                {/* Detalles de la tarea */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                                  <div>
                                    <span className="font-medium">Fecha Límite:</span> {task.dueDate ? formatDateOnly(task.dueDate) : 'Sin fecha'}
                                  </div>
                                  <div>
                                    <span className="font-medium">Asignado a:</span> {task.assignedTo || 'Sin asignar'}
                                  </div>
                                  <div>
                                    <span className="font-medium">Célula:</span> {task.cell || 'Sin asignar'}
                                  </div>
                                  <div>
                                    <span className="font-medium">Plataforma:</span> {task.platform || 'Sin especificar'}
                                  </div>
                                  <div>
                                    <span className="font-medium">Área:</span> {task.area || 'Sin especificar'}
                                  </div>
                                  <div>
                                    <span className="font-medium">Prioridad:</span> {task.priority || 'Media'}
                                  </div>
                                </div>

                                <div className="text-sm text-gray-500 mb-4">
                                  <span className="font-medium">Creada:</span> {task.createdAt ? formatDateOnly(task.createdAt) + ', ' + new Date(task.createdAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : 'Sin fecha de creación'}
                                </div>

                                {/* Sección de evidencias y archivos */}
                                {(task.evidenceFile || task.audioFile || task.evidenceLink || task.evidenceType) && (
                                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                    <h4 className="text-sm font-medium text-blue-800 mb-2">📎 Evidencias y Archivos</h4>
                                    <div className="space-y-3 text-sm">
                                      {task.evidenceType && (
                                        <div>
                                          <span className="font-medium">Tipo de evidencia:</span> {task.evidenceType === 'image' ? '🖼️ Imagen' : task.evidenceType === 'link' ? '🔗 Enlace' : task.evidenceType}
                                        </div>
                                      )}
                                      
                                      {/* Imagen con visualización */}
                                      {task.evidenceFile && (
                                        <div className="space-y-2">
                                          <div className="flex items-center space-x-2">
                                            <span>🖼️</span>
                                            <span className="font-medium">Imagen:</span>
                                            <span className="text-blue-700">{task.evidenceFileName}</span>
                                            <span className="text-gray-500">({Math.round(task.evidenceFileSize / 1024)} KB)</span>
                                            <button
                                              onClick={() => {
                                                setSelectedImage({
                                                  url: getFileUrl(task.evidenceFile),
                                                  name: task.evidenceFileName
                                                });
                                                setShowImageModal(true);
                                              }}
                                              className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 border border-blue-300 rounded"
                                            >
                                              👁️ Ver
                                            </button>
                                          </div>
                                          {/* Miniatura de la imagen */}
                                          <div className="mt-2">
                                            <img 
                                              src={getFileUrl(task.evidenceFile)}
                                              alt={task.evidenceFileName}
                                              className="max-w-32 max-h-32 object-cover rounded border cursor-pointer hover:opacity-80"
                                              onClick={() => {
                                                setSelectedImage({
                                                  url: getFileUrl(task.evidenceFile),
                                                  name: task.evidenceFileName
                                                });
                                                setShowImageModal(true);
                                              }}
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                              }}
                                            />
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Audio con reproductor */}
                                      {task.audioFile && (
                                        <div className="space-y-2">
                                          <div className="flex items-center space-x-2">
                                            <span>🎵</span>
                                            <span className="font-medium">Audio:</span>
                                            <span className="text-blue-700">{task.audioFileName}</span>
                                            <span className="text-gray-500">({Math.round(task.audioFileSize / 1024)} KB)</span>
                                            <button
                                              onClick={() => {
                                                const audioUrl = getFileUrl(task.audioFile);
                                                const audio = new Audio(audioUrl);
                                                audio.play().catch(err => {
                                                  console.error('Error reproduciendo audio:', err);
                                                  alert('Error al reproducir el audio');
                                                });
                                              }}
                                              className="text-green-600 hover:text-green-800 text-xs px-2 py-1 border border-green-300 rounded"
                                            >
                                              ▶️ Reproducir
                                            </button>
                                            <button
                                              onClick={() => transcribeAudio(task)}
                                              disabled={isTranscribing}
                                              className={`text-xs px-2 py-1 border rounded ${
                                                isTranscribing 
                                                  ? 'text-gray-400 border-gray-300 cursor-not-allowed' 
                                                  : 'text-blue-600 hover:text-blue-800 border-blue-300'
                                              }`}
                                            >
                                              {isTranscribing && selectedTaskForTranscription?.id === task.id ? '🔄 Transcribiendo...' : '🎤 Transcribir'}
                                            </button>
                                            {transcriptionResults[task.id] && (
                                              <button
                                                onClick={() => clearTranscription(task.id)}
                                                className="text-red-600 hover:text-red-800 text-xs px-2 py-1 border border-red-300 rounded"
                                              >
                                                🗑️ Limpiar
                                              </button>
                                            )}
                                          </div>
                                          {/* Reproductor de audio funcional */}
                                          <div className="mt-2">
                                            <audio 
                                              controls 
                                              className="w-full max-w-md"
                                              preload="metadata"
                                            >
                                              <source src={getFileUrl(task.audioFile)} type="audio/wav" />
                                              <source src={getFileUrl(task.audioFile)} type="audio/mpeg" />
                                              Tu navegador no soporta el elemento de audio.
                                            </audio>
                                          </div>
                                          
                                          {/* Mostrar transcripción temporal directamente debajo del audio */}
                                          {transcriptionResults[task.id] && (
                                            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
                                              <div className="flex items-center justify-between mb-2">
                                                <span className="font-medium text-sm text-blue-700">
                                                  {transcriptionResults[task.id].isComplete ? '📝 Transcripción:' : '🔄 Transcribiendo...'}
                                                </span>
                                                <button
                                                  onClick={() => clearTranscription(task.id)}
                                                  className="text-red-600 hover:text-red-800 text-xs"
                                                >
                                                  ✕
                                                </button>
                                              </div>
                                              <div className="text-sm text-blue-800 bg-white p-2 rounded border">
                                                {transcriptionResults[task.id].final}
                                                {transcriptionResults[task.id].interim && (
                                                  <span className="text-blue-500 italic"> {transcriptionResults[task.id].interim}</span>
                                                )}
                                                {!transcriptionResults[task.id].final && !transcriptionResults[task.id].interim && (
                                                  <span className="text-gray-500 italic">Iniciando transcripción...</span>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Enlace externo */}
                                      {task.evidenceLink && (
                                        <div className="flex items-center space-x-2">
                                          <span>🔗</span>
                                          <span className="font-medium">Enlace:</span>
                                          <a href={task.evidenceLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                            Ver evidencia externa
                                          </a>
                                        </div>
                                      )}
                                      
                                      {/* Explicación de audio */}
                                      {task.audioExplanation && (
                                        <div className="bg-white p-2 rounded border">
                                          <span className="font-medium">Explicación de audio:</span> 
                                          <p className="mt-1 text-gray-700">{task.audioExplanation}</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Sección de comentarios en historial del cliente */}
                                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                                  <div className="flex items-center justify-between mb-3">
                                    <h4 className="text-sm font-medium text-gray-800">💬 Comentarios de la Tarea</h4>
                                    {loadingComments[task.id] && (
                                      <span className="text-xs text-gray-500">🔄 Cargando comentarios...</span>
                                    )}
                                  </div>
                                  
                                  {/* Lista de comentarios existentes con estructura jerárquica */}
                                  <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                                    {taskComments[task.id] && taskComments[task.id].length > 0 ? (
                                      (() => {
                                        // Separar comentarios principales y respuestas usando parent_comment_id
                                        const mainComments = [];
                                        const replies = {};

                                        // Primero, ordenar todos los comentarios por fecha de creación
                                        const sortedComments = [...taskComments[task.id]].sort((a, b) => {
                                          const dateA = new Date(a.createdAt || a.created_at);
                                          const dateB = new Date(b.createdAt || b.created_at);
                                          return dateA - dateB;
                                        });

                                        sortedComments.forEach(comment => {
                                          // Usar parent_comment_id para determinar si es una respuesta
                                          const parentId = comment.parent_comment_id || comment.parentCommentId;

                                          if (parentId) {
                                            // Es una respuesta - agrupar por parent_comment_id
                                            if (!replies[parentId]) replies[parentId] = [];
                                            replies[parentId].push(comment);
                                          } else {
                                            // Es un comentario principal
                                            mainComments.push(comment);
                                          }
                                        });
                                        
                                        return mainComments.map((comment, index) => (
                                          <div key={comment.id || index} className="space-y-2">
                                            {/* Comentario principal */}
                                            <div className="bg-white p-3 rounded border text-sm">
                                              <div className="flex items-center justify-between mb-2">
                                                <span className="font-medium text-gray-900">
                                                  👤 {comment.userName || comment.user_name || 'Usuario Anónimo'}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                  {formatDateOnly(comment.createdAt || comment.created_at)} {new Date(comment.createdAt || comment.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                              </div>
                                              
                                              {/* Texto del comentario con enlaces clickeables */}
                                              {comment.comment && (
                                                <div className="text-gray-700 mb-2">
                                                  {renderTextWithLinks(comment.comment)}
                                                </div>
                                              )}
                                              
                                              {/* Archivo adjunto si existe */}
                                              {(comment.fileName || comment.file_name) && (
                                                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                                  <div className="flex items-center space-x-2">
                                                    <span className="text-blue-600">📎</span>
                                                    <span className="text-sm font-medium text-blue-800">Archivo adjunto:</span>
                                                    <span className="text-sm text-blue-700">{comment.fileName || comment.file_name}</span>
                                                    {(comment.filePath || comment.file_path) && (
                                                        <a 
                                                          href={getFileUrl(comment.filePath || comment.file_path)}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                                        >
                                                          📥 Descargar
                                                        </a>
                                                    )}
                                                  </div>
                                                  
                                                  {/* Mostrar vista previa si es imagen */}
                                                  {(comment.filePath || comment.file_path) && (comment.fileName || comment.file_name) && 
                                                   (comment.fileName || comment.file_name).toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                                                    <div className="mt-2">
                                                        <img 
                                                          src={getFileUrl(comment.filePath || comment.file_path)}
                                                          alt={comment.fileName || comment.file_name}
                                                          className="max-w-32 max-h-32 object-cover rounded border cursor-pointer hover:opacity-80"
                                                          onClick={() => {
                                                            setSelectedImage({
                                                              url: getFileUrl(comment.filePath || comment.file_path),
                                                              name: comment.fileName || comment.file_name
                                                            });
                                                            setShowImageModal(true);
                                                          }}
                                                          onError={(e) => {
                                                            e.target.style.display = 'none';
                                                          }}
                                                        />
                                                    </div>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                            
                                            {/* Respuestas al comentario principal */}
                                            {replies[comment.id] && replies[comment.id].length > 0 && (
                                              <div className="ml-6 space-y-2">
                                                {replies[comment.id].map((reply, replyIndex) => (
                                                  <div key={reply.id || `reply-${replyIndex}`} className="bg-gray-50 p-3 rounded border-l-4 border-blue-300 text-sm">
                                                    <div className="flex items-center justify-between mb-2">
                                                      <span className="font-medium text-gray-800 flex items-center">
                                                        <span className="text-blue-600 mr-1">↳</span>
                                                        👤 {reply.userName || reply.user_name || 'Usuario Anónimo'}
                                                      </span>
                                                      <span className="text-xs text-gray-500">
                                                        {formatDateOnly(reply.createdAt || reply.created_at)} {new Date(reply.createdAt || reply.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                                      </span>
                                                    </div>
                                                    
                                                    {/* Texto de la respuesta */}
                                                    {reply.comment && (
                                                      <p className="text-gray-700 mb-2">{reply.comment}</p>
                                                    )}
                                                    
                                                    {/* Archivo adjunto en respuesta si existe */}
                                                    {(reply.fileName || reply.file_name) && (
                                                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                                        <div className="flex items-center space-x-2">
                                                          <span className="text-blue-600">📎</span>
                                                          <span className="text-sm font-medium text-blue-800">Archivo adjunto:</span>
                                                          <span className="text-sm text-blue-700">{reply.fileName || reply.file_name}</span>
                                                          {(reply.filePath || reply.file_path) && (
                                                              <a 
                                                                href={getFileUrl(reply.filePath || reply.file_path)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                                              >
                                                                📥 Descargar
                                                              </a>
                                                          )}
                                                        </div>
                                                        
                                                        {/* Vista previa de imagen en respuesta */}
                                                        {(reply.filePath || reply.file_path) && (reply.fileName || reply.file_name) && 
                                                         (reply.fileName || reply.file_name).toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                                                          <div className="mt-2">
                                                              <img 
                                                                src={getFileUrl(reply.filePath || reply.file_path)}
                                                                alt={reply.fileName || reply.file_name}
                                                                className="max-w-20 max-h-20 object-cover rounded border cursor-pointer hover:opacity-80"
                                                                onClick={() => {
                                                                  setSelectedImage({
                                                                    url: getFileUrl(reply.filePath || reply.file_path),
                                                                    name: reply.fileName || reply.file_name
                                                                  });
                                                                  setShowImageModal(true);
                                                                }}
                                                                onError={(e) => {
                                                                  e.target.style.display = 'none';
                                                                }}
                                                              />
                                                          </div>
                                                        )}
                                                      </div>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ));
                                      })()
                                    ) : (
                                      <div className="text-xs text-gray-500 italic">No hay comentarios para esta tarea</div>
                                    )}
                                  </div>
                                </div>

                                {/* Estado visual */}
                                <div className="flex items-center space-x-2">
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                                    {getPriorityEmoji(task.priority)} {task.priority === 'alta' ? 'Alta' : task.priority === 'media' ? 'Media' : task.priority === 'baja' ? 'Baja' : task.priority === 'urgente' ? 'Urgente' : 'Media'}
                                  </span>
                                  {(() => {
                                    const realStatus = getTaskRealStatus(task);
                                    return (
                                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRealStatusColor(realStatus.type)}`}>
                                        {realStatus.text}
                                      </span>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Editar Tarea */}
      {showEditTaskModal && editingTask && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Editar Tarea</h2>
                <button 
                  onClick={() => {
                    setShowEditTaskModal(false);
                    setEditingTask(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                console.log('🔧 Actualizando tarea:', editingTask.id);
                
                try {
                  // Preparar datos para enviar al servidor con TODOS los campos
                  const updateData = {
                    title: editingTask.title || editingTask.description,
                    description: editingTask.description || editingTask.title,
                    points: editingTask.points || editingTask.description,
                    assignedTo: editingTask.assignedTo,
                    dueDate: editingTask.dueDate || null,
                    priority: editingTask.priority || 'media',
                    cell: editingTask.cell || '',
                    platform: editingTask.platform || '',
                    area: editingTask.area || '',
                    publicityAccountLink: editingTask.publicityAccountLink || '',
                    websiteLink: editingTask.websiteLink || '',
                    audioExplanation: editingTask.audioExplanation || '',
                    completed: editingTask.completed ? 1 : 0,
                    completionComment: editingTask.completionComment || '',
                    status: editingTask.status || (editingTask.completed ? 'completada' : 'pendiente'),
                    updatedAt: new Date().toISOString()
                  };

                  console.log('📤 Enviando datos al servidor:', updateData);

                  // Actualizar la tarea en el servidor
                  const response = await apiFetch(`/tasks/${editingTask.id}`, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(updateData)
                  });

                  if (!response.ok) {
                    const errorText = await response.text();
                    console.error('❌ Error del servidor:', errorText);
                    throw new Error('Error al actualizar la tarea en el servidor');
                  }

                  const updatedTaskFromServer = await response.json();
                  console.log('✅ Tarea actualizada en servidor:', updatedTaskFromServer);

                  // FORZAR RECARGA COMPLETA DE DATOS DESDE EL SERVIDOR
                  console.log('🔄 Forzando recarga completa de datos...');
                  await reloadData();
                  
                  // Cerrar modal y limpiar estado
                  setShowEditTaskModal(false);
                  setEditingTask(null);
                  
                  alert('Tarea actualizada exitosamente');
                  
                } catch (error) {
                  console.error('❌ Error updating task:', error);
                  alert('Error al actualizar la tarea: ' + error.message);
                }
              }}>
                <div className="space-y-4">
                  {/* Título */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Título *</label>
                    <input
                      type="text"
                      value={editingTask.title || editingTask.description}
                      onChange={(e) => setEditingTask({...editingTask, title: e.target.value, description: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Título de la tarea"
                      required
                    />
                  </div>

                  {/* Descripción/Puntos */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Descripción (Puntos) *</label>
                    <textarea
                      value={editingTask.points || editingTask.description}
                      onChange={(e) => setEditingTask({...editingTask, points: e.target.value, description: e.target.value})}
                      rows="4"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Describe los puntos de la tarea (uno por línea)..."
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Escribe cada punto en una línea separada
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Cliente</label>
                      <input
                        type="text"
                        value={editingTask.clientName}
                        disabled
                        className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100 text-gray-600"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Asignado a *</label>
                      <select
                        value={editingTask.assignedTo}
                        onChange={(e) => setEditingTask({...editingTask, assignedTo: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="">Selecciona un usuario</option>
                        {systemUsers.map(user => (
                          <option key={user.id} value={`${user.firstName} ${user.lastName}`}>
                            {user.firstName} {user.lastName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Límite</label>
                      <input
                        type="date"
                        value={editingTask.dueDate}
                        onChange={(e) => setEditingTask({...editingTask, dueDate: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prioridad</label>
                      <select
                        value={editingTask.priority}
                        onChange={(e) => setEditingTask({...editingTask, priority: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="baja">🟢 Baja</option>
                        <option value="media">🟡 Media</option>
                        <option value="alta">🟠 Alta</option>
                        <option value="urgente">🔴 Urgente</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Célula</label>
                      <select
                        value={editingTask.cell}
                        onChange={(e) => setEditingTask({...editingTask, cell: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleccionar...</option>
                        {['C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA', 'C7GA', 'C8GA', 'C1MA', 'C2MA', 'C3MA', 'C4MA', 'C5MA'].map(cell => (
                          <option key={cell} value={cell}>{cell}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Plataforma</label>
                      <select
                        value={editingTask.platform}
                        onChange={(e) => setEditingTask({...editingTask, platform: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Google Ads">Google Ads</option>
                        <option value="Meta Ads">Meta Ads</option>
                        <option value="Ninguna">Ninguna</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Área</label>
                      <select
                        value={editingTask.area}
                        onChange={(e) => setEditingTask({...editingTask, area: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleccionar...</option>
                        <option value="PROGRAMACION FRONT END (SITIOS WEB NUEVOS)">PROGRAMACION FRONT END (SITIOS WEB NUEVOS)</option>
                        <option value="PROGRAMACION DESARROLLO DE PRODUCTO">PROGRAMACION DESARROLLO DE PRODUCTO</option>
                        <option value="DESARROLLO WEB LANDING PAGE">DESARROLLO WEB LANDING PAGE</option>
                        <option value="SOPORTE WEB">SOPORTE WEB</option>
                        <option value="DESARROLLO WEB SEO">DESARROLLO WEB SEO</option>
                        <option value="DISEÑO GRAFICO">DISEÑO GRAFICO</option>
                        <option value="VIDEO MKT">VIDEO MKT</option>
                        <option value="GOOGLE ADS - MKT">GOOGLE ADS - MKT</option>
                        <option value="META ADS - MKT">META ADS - MKT</option>
                        <option value="GOOGLE MAPS">GOOGLE MAPS</option>
                        <option value="CAPACITACION">CAPACITACION</option>
                        <option value="ADMIN (CLIENTES)">ADMIN (CLIENTES)</option>
                        <option value="AGENDAMIENTO Y CITAS">AGENDAMIENTO Y CITAS</option>
                        <option value="COBRANZA">COBRANZA</option>
                        <option value="COFEPRIS Y BACK LINKS">COFEPRIS Y BACK LINKS</option>
                        <option value="EXITO DE LA ATENCION DEL CLIENTE">EXITO DE LA ATENCION DEL CLIENTE</option>
                        <option value="VENTAS">VENTAS</option>
                        <option value="CONTABILIDAD">CONTABILIDAD</option>
                        <option value="DIRECTOR GENERAL">DIRECTOR GENERAL</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Estado Actual</label>
                      <select
                        value={editingTask.status || (editingTask.completed ? 'completada' : 'pendiente')}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          setEditingTask({
                            ...editingTask, 
                            status: newStatus,
                            completed: newStatus === 'completada'
                          });
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="pendiente">⏳ Pendiente</option>
                        <option value="en_progreso">🔄 En Progreso</option>
                        <option value="completada">✅ Completada</option>
                        <option value="cancelada">❌ Cancelada</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Link Cuenta Publicitaria</label>
                      <input
                        type="url"
                        value={editingTask.publicityAccountLink || ''}
                        onChange={(e) => setEditingTask({...editingTask, publicityAccountLink: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://ads.google.com/..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Link Sitio Web</label>
                      <input
                        type="url"
                        value={editingTask.websiteLink || ''}
                        onChange={(e) => setEditingTask({...editingTask, websiteLink: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://www.ejemplo.com"
                      />
                    </div>
                  </div>

                  {/* Explicación de Audio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Explicación de Audio</label>
                    <textarea
                      value={editingTask.audioExplanation || ''}
                      onChange={(e) => setEditingTask({...editingTask, audioExplanation: e.target.value})}
                      rows="2"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Descripción del audio explicativo..."
                    />
                  </div>

                  {(editingTask.completed || editingTask.status === 'completada') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Comentario de Finalización</label>
                      <textarea
                        value={editingTask.completionComment || ''}
                        onChange={(e) => setEditingTask({...editingTask, completionComment: e.target.value})}
                        rows="2"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Describe cómo se completó la tarea..."
                      />
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditTaskModal(false);
                      setEditingTask(null);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Actualizar Tarea
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Nueva Tarea */}
      {showNewTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Nueva Tarea</h2>
                <button 
                  onClick={() => setShowNewTaskModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                console.log('\n🚀 === FRONTEND: Creando nueva tarea ===');
                console.log('📝 Estado newTask completo:', JSON.stringify(newTask, null, 2));
                console.log('🎵 AudioBlob presente:', !!audioBlob);
                if (audioBlob) {
                  console.log('  - Tamaño del audio:', audioBlob.size, 'bytes');
                  console.log('  - Tipo del audio:', audioBlob.type);
                }
                
                try {
                  // Validar campos requeridos
                  console.log('✅ Validando campos requeridos...');
                  if (!newTask.title) {
                    console.log('❌ Falta título');
                    alert('Por favor ingresa un título');
                    return;
                  }
                  if (!newTask.assignedTo) {
                    console.log('❌ Falta usuario asignado');
                    alert('Por favor selecciona un usuario asignado');
                    return;
                  }
                  if (!newTask.clientId) {
                    console.log('❌ Falta cliente');
                    alert('Por favor selecciona un cliente');
                    return;
                  }
                  console.log('✅ Validación de campos completada');

                  // Buscar el cliente seleccionado
                  console.log('🔍 Buscando cliente con ID:', newTask.clientId);
                  const selectedClient = allClients.find(c => c.id === parseInt(newTask.clientId));
                  console.log('👤 Cliente encontrado:', selectedClient?.name);
                  
                  if (!selectedClient) {
                    console.log('❌ Cliente no encontrado');
                    alert('Cliente no encontrado.');
                    return;
                  }

                  // Siempre crear una junta virtual para la tarea (sin restricciones)
                  const latestMeeting = {
                    id: `virtual-${Date.now()}`,
                    date: new Date().toISOString(),
                    meetingType: 'Tarea independiente'
                  };
                  console.log('📅 Creando junta virtual:', latestMeeting.id);

                  // Procesar puntos como texto
                  const pointsText = newTask.points.filter(point => point.trim()).join('\n');
                  console.log('📝 Puntos procesados:', pointsText);

                  // Crear FormData para manejar archivos y audio
                  console.log('📦 Creando FormData...');
                  const formData = new FormData();
                  
                  // Agregar datos básicos de la tarea (INCLUYENDO clientId)
                  const basicData = {
                    meetingId: latestMeeting.id,
                    clientId: newTask.clientId, // ← AGREGADO: clientId
                    title: newTask.title,
                    description: newTask.description || pointsText,
                    points: pointsText,
                    assignedTo: newTask.assignedTo,
                    dueDate: newTask.dueDate || '',
                    priority: newTask.priority,
                    publicityAccountLink: newTask.publicityAccountLink || '',
                    websiteLink: newTask.websiteLink || '',
                    cell: newTask.cell || '',
                    platform: newTask.platform || '',
                    area: newTask.area || '',
                    evidenceType: newTask.evidenceType || '',
                    audioExplanation: newTask.audioExplanation || '',
                    evidenceStatus: 'pending',
                    completed: false,
                    completionComment: '',
                    createdBy: currentUser?.name || 'Usuario'
                  };
                  
                  console.log('📋 Datos básicos a enviar:', JSON.stringify(basicData, null, 2));
                  
                  // Agregar cada campo al FormData
                  Object.entries(basicData).forEach(([key, value]) => {
                    formData.append(key, value);
                    console.log(`  - ${key}: "${value}"`);
                  });

                  // Agregar archivo de evidencia si existe
                  console.log('🖼️ Procesando evidencia...');
                  if (newTask.evidenceFile) {
                    console.log('  - Archivo de evidencia presente:', newTask.evidenceFileName);
                    formData.append('evidenceFile', newTask.evidenceFile);
                    formData.append('evidenceFileName', newTask.evidenceFileName);
                    formData.append('evidenceFileSize', newTask.evidenceFileSize.toString());
                    console.log('  - Evidencia agregada al FormData');
                  } else {
                    console.log('  - No hay archivo de evidencia');
                  }

                  // Agregar enlace de evidencia si existe
                  if (newTask.evidenceLink) {
                    console.log('  - Enlace de evidencia:', newTask.evidenceLink);
                    formData.append('evidenceLink', newTask.evidenceLink);
                  } else {
                    console.log('  - No hay enlace de evidencia');
                  }

                  // Agregar audio si existe
                  console.log('🎵 Procesando audio...');
                  if (audioBlob) {
                    const audioFile = new File([audioBlob], `audio-${Date.now()}.wav`, { type: 'audio/wav' });
                    formData.append('audioFile', audioFile);
                    console.log('  - Audio agregado al FormData:', audioFile.name, audioFile.size, 'bytes');
                  } else {
                    console.log('  - No hay audio');
                  }

                  // Mostrar contenido del FormData
                  console.log('\n📦 Contenido final del FormData:');
                  for (let [key, value] of formData.entries()) {
                    if (value instanceof File) {
                      console.log(`  - ${key}: [File] ${value.name} (${value.size} bytes, ${value.type})`);
                    } else {
                      console.log(`  - ${key}: "${value}"`);
                    }
                  }

                  console.log('\n🌐 Enviando petición al servidor...');
                  // Crear la tarea en el servidor
                  const response = await apiFetch('/tasks', {
                    method: 'POST',
                    body: formData // Usar FormData en lugar de JSON
                  });
                  
                  console.log('📡 Respuesta del servidor:', response.status, response.statusText);

                  if (!response.ok) {
                    throw new Error('Error al crear la tarea en el servidor');
                  }

                  const result = await response.json();
                  console.log('Tarea creada exitosamente:', result);

                  // Crear el objeto de tarea para el estado local
                  const newTaskObj = {
                    id: result.id,
                    meetingId: latestMeeting.id,
                    title: newTask.title,
                    description: newTask.description || pointsText,
                    points: pointsText,
                    assignedTo: newTask.assignedTo,
                    dueDate: newTask.dueDate || null,
                    priority: newTask.priority,
                    publicityAccountLink: newTask.publicityAccountLink,
                    websiteLink: newTask.websiteLink,
                    cell: newTask.cell,
                    platform: newTask.platform,
                    area: newTask.area,
                    evidenceType: newTask.evidenceType,
                    audioExplanation: newTask.audioExplanation,
                    completed: false,
                    completionComment: '',
                    createdBy: currentUser?.name || 'Usuario',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                  };

                  // FORZAR RECARGA COMPLETA DE DATOS DESDE EL SERVIDOR
                  console.log('🔄 Forzando recarga completa de datos...');
                  await reloadData();

                  // Limpiar el formulario y cerrar modal
                  setNewTask({
                    title: '',
                    description: '',
                    points: [''],
                    assignedTo: '',
                    dueDate: '',
                    priority: 'media',
                    clientId: '',
                    meetingId: '',
                    publicityAccountLink: '',
                    websiteLink: '',
                    cell: '',
                    platform: '',
                    area: '',
                    evidenceType: '',
                    audioExplanation: ''
                  });
                  setAudioBlob(null); // Limpiar audio también
                  setShowNewTaskModal(false);
                  
                  alert('Tarea creada exitosamente');
                  
                } catch (error) {
                  console.error('Error creating task:', error);
                  alert('Error al crear la tarea: ' + error.message);
                }
              }}>
                <div className="space-y-4">
                  {/* Título */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Título</label>
                    <input
                      type="text"
                      value={newTask.title}
                      onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Título de la tarea"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Asignado a - Buscador en tiempo real */}
                    <div className="relative user-search-container">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Asignado a</label>
                      <input
                        type="text"
                        value={userSearch}
                        onChange={(e) => {
                          setUserSearch(e.target.value);
                          setShowUserDropdown(true);
                        }}
                        onFocus={() => setShowUserDropdown(true)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Buscar usuario..."
                        required
                      />
                      
                      {/* Dropdown de usuarios */}
                      {showUserDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {systemUsers
                            .filter(user => {
                              const fullName = `${user.firstName} ${user.lastName}`;
                              return fullName.toLowerCase().includes(userSearch.toLowerCase()) ||
                                     user.role.toLowerCase().includes(userSearch.toLowerCase());
                            })
                            .map(user => (
                              <button
                                key={user.id}
                                type="button"
                                onClick={() => {
                                  const fullName = `${user.firstName} ${user.lastName}`;
                                  setNewTask({...newTask, assignedTo: fullName});
                                  setUserSearch(fullName);
                                  setShowUserDropdown(false);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium">{user.firstName} {user.lastName}</span>
                                  <span className="text-sm text-gray-500">
                                    {user.role === 'admin' ? 'Administrador' : 'Ejecutivo'}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-400">{user.email}</div>
                              </button>
                            ))
                          }
                          {systemUsers.filter(user => {
                            const fullName = `${user.firstName} ${user.lastName}`;
                            return fullName.toLowerCase().includes(userSearch.toLowerCase()) ||
                                   user.role.toLowerCase().includes(userSearch.toLowerCase());
                          }).length === 0 && (
                            <div className="px-3 py-2 text-gray-500 text-sm">
                              No se encontraron usuarios
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Link Cuenta Publicitaria */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Link Cuenta Publicitaria (Opcional)</label>
                      <input
                        type="url"
                        value={newTask.publicityAccountLink}
                        onChange={(e) => setNewTask({...newTask, publicityAccountLink: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://ads.google.com/..."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Cliente - Buscador en tiempo real */}
                    <div className="relative client-search-container">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Cliente</label>
                      <input
                        type="text"
                        value={clientSearch}
                        onChange={(e) => {
                          setClientSearch(e.target.value);
                          setShowClientDropdown(true);
                        }}
                        onFocus={() => setShowClientDropdown(true)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Buscar cliente..."
                        required
                      />
                      
                      {/* Dropdown de clientes */}
                      {showClientDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {allClients
                            .filter(client => {
                              // Filtrar por búsqueda
                              const matchesSearch = client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                                     (client.email && client.email.toLowerCase().includes(clientSearch.toLowerCase())) ||
                                     (client.phone && client.phone.includes(clientSearch));
                              
                              // Filtrar por células del usuario estándar
                              if (currentUser?.role === 'standard' && currentUser?.cells && currentUser.cells.length > 0) {
                                const clientCells = client.cells || [client.cell];
                                const hasAllowedCell = clientCells.some(clientCell => 
                                  currentUser.cells.includes(clientCell)
                                );
                                return matchesSearch && hasAllowedCell;
                              }
                              
                              // Administradores ven todos los clientes
                              return matchesSearch;
                            })
                            .map(client => (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => {
                                  setNewTask({...newTask, clientId: client.id});
                                  setClientSearch(client.name);
                                  setShowClientDropdown(false);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium">{client.name}</span>
                                  <span className="text-sm text-gray-500">
                                    ID: {client.id}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-400">
                                  {client.email && <span>{client.email}</span>}
                                  {client.phone && <span> • {client.phone}</span>}
                                </div>
                              </button>
                            ))
                          }
                          {allClients.filter(client => {
                            return client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                                   (client.email && client.email.toLowerCase().includes(clientSearch.toLowerCase())) ||
                                   (client.phone && client.phone.includes(clientSearch));
                          }).length === 0 && (
                            <div className="px-3 py-2 text-gray-500 text-sm">
                              No se encontraron clientes
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Link Sitio Web */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Link Sitio Web (Opcional)</label>
                      <input
                        type="url"
                        value={newTask.websiteLink}
                        onChange={(e) => setNewTask({...newTask, websiteLink: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://www.ejemplo.com"
                      />
                    </div>
                  </div>

                  {/* Descripción (Puntos) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Descripción (Puntos)</label>
                    <div className="space-y-2">
                      {newTask.points.map((point, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500 w-6">{index + 1}.</span>
                          <input
                            type="text"
                            value={point}
                            onChange={(e) => {
                              const updatedPoints = [...newTask.points];
                              updatedPoints[index] = e.target.value;
                              setNewTask({...newTask, points: updatedPoints});
                            }}
                            className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder={`Punto ${index + 1}`}
                          />
                          {newTask.points.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const updatedPoints = newTask.points.filter((_, i) => i !== index);
                                setNewTask({...newTask, points: updatedPoints});
                              }}
                              className="text-red-600 hover:text-red-800 px-2 py-1"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => setNewTask({...newTask, points: [...newTask.points, '']})}
                        className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-300 rounded-md"
                      >
                        Añadir Punto
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Célula */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Célula</label>
                      <select
                        value={newTask.cell}
                        onChange={(e) => setNewTask({...newTask, cell: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleccionar...</option>
                        {(() => {
                          // Filtrar células según el rol del usuario
                          const allCells = ['C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA', 'C1MA', 'C2MA', 'C3MA', 'C4MA', 'G1M1', 'G1M2', 'G1M3', 'G1M4', 'G2M1', 'G2M2', 'G2M3', 'G2M4', 'G3M1', 'G3M2', 'G3M3', 'G3M4', 'G4M1', 'G4M2', 'G4M3', 'G4M4', 'G5M1', 'G5M2', 'G5M3', 'G5M4', 'G6M1', 'G6M2', 'G6M3', 'G6M4', 'G7M1', 'G7M2', 'G7M3', 'G7M4'];
                          
                          // Si es usuario estándar, solo mostrar sus células asignadas
                          if (currentUser?.role === 'standard' && currentUser?.cells && currentUser.cells.length > 0) {
                            return currentUser.cells.map(cell => (
                              <option key={cell} value={cell}>{cell}</option>
                            ));
                          }
                          
                          // Si es administrador, mostrar todas las células
                          return allCells.map(cell => (
                            <option key={cell} value={cell}>{cell}</option>
                          ));
                        })()}
                      </select>
                    </div>

                  {/* Tipo de Evidencia */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Evidencia</label>
                    <select
                      value={newTask.evidenceType}
                      onChange={(e) => setNewTask({...newTask, evidenceType: e.target.value, evidenceFile: null, evidenceFileName: '', evidenceLink: ''})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Ninguna</option>
                      <option value="link">Link</option>
                      <option value="image">Imagen</option>
                    </select>
                    
                    {/* Sección de evidencia cuando se selecciona un tipo */}
                    {newTask.evidenceType && newTask.evidenceType !== '' && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <h4 className="text-sm font-medium text-blue-800 mb-2">
                          {newTask.evidenceType === 'link' ? 'Agregar Link' : 'Subir Imagen'}
                        </h4>
                        
                        <div className="space-y-2">
                          {newTask.evidenceType === 'link' ? (
                            // Campo para link
                            <div>
                              <input
                                type="url"
                                value={newTask.evidenceLink || ''}
                                onChange={(e) => setNewTask({...newTask, evidenceLink: e.target.value})}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="https://ejemplo.com/evidencia"
                              />
                              <p className="text-xs text-gray-600 mt-1">
                                Ingresa el enlace de la evidencia (página web, documento en línea, etc.)
                              </p>
                            </div>
                          ) : (
                            // Campo para imagen
                            <div>
                              <input
                                type="file"
                                id="evidenceFileInput"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    setNewTask({
                                      ...newTask, 
                                      evidenceFile: file,
                                      evidenceFileName: file.name,
                                      evidenceFileSize: file.size
                                    });
                                  }
                                }}
                                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                              />
                              
                              {newTask.evidenceFile && (
                                <div className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md mt-2">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-lg">🖼️</span>
                                    <div>
                                      <p className="text-sm font-medium text-gray-900">{newTask.evidenceFileName}</p>
                                      <p className="text-xs text-gray-500">
                                        {(newTask.evidenceFileSize / 1024 / 1024).toFixed(2)} MB
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setNewTask({...newTask, evidenceFile: null, evidenceFileName: '', evidenceFileSize: 0})}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                  >
                                    ✕ Eliminar
                                  </button>
                                </div>
                              )}
                              
                              <p className="text-xs text-gray-600 mt-1">
                                Formatos permitidos: JPG, PNG, GIF, WebP
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Fecha Límite */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fecha Límite</label>
                      <input
                        type="date"
                        value={newTask.dueDate}
                        onChange={(e) => setNewTask({...newTask, dueDate: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="dd/mm/aaaa"
                      />
                    </div>

                    {/* Prioridad */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prioridad</label>
                      <select
                        value={newTask.priority}
                        onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="baja">Baja</option>
                        <option value="media">Media</option>
                        <option value="alta">Alta</option>
                        <option value="urgente">Urgente</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Plataforma */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Plataforma</label>
                      <select 
                        id="platform" 
                        value={newTask.platform}
                        onChange={(e) => setNewTask({...newTask, platform: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all" 
                        required
                      >
                        <option value="Google Ads">Google Ads</option>
                        <option value="Meta Ads">Meta Ads</option>
                        <option value="Ninguna">Ninguna</option>
                      </select>
                    </div>

                    {/* Área */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Área</label>
                      <select
                        value={newTask.area}
                        onChange={(e) => setNewTask({...newTask, area: e.target.value})}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleccionar...</option>
                        <option value="PROGRAMACION FRONT END (SITIOS WEB NUEVOS)">PROGRAMACION FRONT END (SITIOS WEB NUEVOS)</option>
                        <option value="PROGRAMACION DESARROLLO DE PRODUCTO">PROGRAMACION DESARROLLO DE PRODUCTO</option>
                        <option value="DESARROLLO WEB LANDING PAGE">DESARROLLO WEB LANDING PAGE</option>
                        <option value="SOPORTE WEB">SOPORTE WEB</option>
                        <option value="DESARROLLO WEB SEO">DESARROLLO WEB SEO</option>
                        <option value="DISEÑO GRAFICO">DISEÑO GRAFICO</option>
                        <option value="VIDEO MKT">VIDEO MKT</option>
                        <option value="GOOGLE ADS - MKT">GOOGLE ADS - MKT</option>
                        <option value="META ADS - MKT">META ADS - MKT</option>
                        <option value="GOOGLE MAPS">GOOGLE MAPS</option>
                        <option value="CAPACITACION">CAPACITACION</option>
                        <option value="ADMIN (CLIENTES)">ADMIN (CLIENTES)</option>
                        <option value="AGENDAMIENTO Y CITAS">AGENDAMIENTO Y CITAS</option>
                        <option value="COBRANZA">COBRANZA</option>
                        <option value="COFEPRIS Y BACK LINKS">COFEPRIS Y BACK LINKS</option>
                        <option value="EXITO DE LA ATENCION DEL CLIENTE">EXITO DE LA ATENCION DEL CLIENTE</option>
                        <option value="VENTAS">VENTAS</option>
                        <option value="CONTABILIDAD">CONTABILIDAD</option>
                        <option value="DIRECTOR GENERAL">DIRECTOR GENERAL</option>
                      </select>
                    </div>
                  </div>

                  {/* Grabar Audio (Explicación) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Grabar Audio (Explicación)</label>
                    <div className="flex items-center space-x-3">
                      <button
                        type="button"
                        className={`px-4 py-2 rounded-md flex items-center transition-colors ${
                          isRecording 
                            ? 'bg-red-500 text-white hover:bg-red-600' 
                            : 'bg-blue-500 text-white hover:bg-blue-600'
                        }`}
                        onClick={handleAudioRecording}
                      >
                        {isRecording ? '⏹️ Detener Grabación' : '🎤 Grabar Audio'}
                      </button>
                      
                      {/* Controles de audio cuando hay grabación */}
                      {audioBlob && (
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={playAudio}
                            className="bg-green-500 text-white px-3 py-1 rounded-md hover:bg-green-600 text-sm"
                          >
                            ▶️ Reproducir
                          </button>
                          <button
                            type="button"
                            onClick={deleteAudio}
                            className="bg-red-500 text-white px-3 py-1 rounded-md hover:bg-red-600 text-sm"
                          >
                            🗑️ Eliminar
                          </button>
                        </div>
                      )}
                      {newTask.audioExplanation && (
                        <span className="text-sm text-gray-600">Audio grabado: {newTask.audioExplanation}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowNewTaskModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                  >
                    Crear Tarea
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}


      {/* Modal de Notificaciones */}
      {showNotificationsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">🔔 Notificaciones</h2>
                <button 
                  onClick={() => setShowNotificationsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {notifications.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">🔔</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay notificaciones nuevas</h3>
                  <p className="text-gray-500">Te notificaremos cuando tengas nuevas tareas asignadas o comentarios en tus tareas.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notifications.map((notification, index) => (
                    <div 
                      key={notification.id || index}
                      className={`p-4 rounded-lg border transition-colors ${
                        notification.isRead 
                          ? 'bg-gray-50 border-gray-200' 
                          : 'bg-blue-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {notification.type === 'new_task' ? (
                            <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center">
                              📋
                            </div>
                          ) : (
                            <div className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center">
                              💬
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="text-sm font-medium text-gray-900">
                              {notification.title}
                            </h4>
                            <span className="text-xs text-gray-500">
                              {formatDateOnly(notification.createdAt)} {new Date(notification.createdAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          
                          <p className="text-sm text-gray-700 mb-2">
                            {notification.message}
                          </p>
                          
                          {notification.clientName && (
                            <p className="text-xs text-gray-500 mb-2">
                              Cliente: {notification.clientName}
                            </p>
                          )}
                          
                          {notification.priority && (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(notification.priority)}`}>
                              {getPriorityEmoji(notification.priority)} {notification.priority.charAt(0).toUpperCase() + notification.priority.slice(1)}
                            </span>
                          )}
                          
                          {notification.commentText && (
                            <div className="mt-2 p-2 bg-white border border-gray-200 rounded text-xs">
                              <span className="font-medium">Comentario:</span> "{notification.commentText}"
                            </div>
                          )}
                          
                          {notification.taskId && (
                            <div className="mt-3">
                              <button
                                onClick={() => goToTask(notification.taskId)}
                                className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                              >
                                Ver Tarea
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 flex justify-between items-center">
                <span className="text-sm text-gray-500">
                  {notifications.length > 0 && `${notifications.length} notificación${notifications.length !== 1 ? 'es' : ''}`}
                </span>
                <button
                  onClick={() => {
                    markNotificationsAsRead();
                    setShowNotificationsModal(false);
                  }}
                  className="text-sm px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                >
                  Marcar como leídas
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalles de Tarea */}
      {showTaskDetailsModal && selectedTaskForDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Detalles de la Tarea</h2>
                <button 
                  onClick={() => {
                    setShowTaskDetailsModal(false);
                    setSelectedTaskForDetails(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Información básica */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{selectedTaskForDetails.title || selectedTaskForDetails.description}</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Cliente:</span>
                      <span className="ml-2 text-gray-900">{selectedTaskForDetails.clientName}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Asignado a:</span>
                      <span className="ml-2 text-gray-900">{selectedTaskForDetails.assignedTo || 'Sin asignar'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Fecha límite:</span>
                      <span className="ml-2 text-gray-900">{selectedTaskForDetails.dueDate ? formatDateOnly(selectedTaskForDetails.dueDate) : 'Sin fecha'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Célula:</span>
                      <span className="ml-2 text-gray-900">{selectedTaskForDetails.cell || 'Sin asignar'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Plataforma:</span>
                      <span className="ml-2 text-gray-900">{selectedTaskForDetails.platform || 'Sin especificar'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Área:</span>
                      <span className="ml-2 text-gray-900">{selectedTaskForDetails.area || 'Sin especificar'}</span>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center space-x-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(selectedTaskForDetails.priority)}`}>
                      {getPriorityEmoji(selectedTaskForDetails.priority)} {selectedTaskForDetails.priority?.charAt(0).toUpperCase() + selectedTaskForDetails.priority?.slice(1)}
                    </span>
                    {(() => {
                      const realStatus = getTaskRealStatus(selectedTaskForDetails);
                      return (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getRealStatusColor(realStatus.type)}`}>
                          {realStatus.text}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                {/* Descripción detallada */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">📝 Descripción</h4>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    {selectedTaskForDetails.points && selectedTaskForDetails.points.trim() ? (
                      selectedTaskForDetails.points.split('\n').filter(point => point.trim()).map((point, pointIndex) => (
                        <div key={pointIndex} className="mb-2">
                          <span className="font-medium text-gray-700">{pointIndex + 1}.</span> 
                          <span className="ml-2 text-gray-900">{point.trim()}</span>
                        </div>
                      ))
                    ) : selectedTaskForDetails.description && selectedTaskForDetails.description.includes('\n') ? (
                      selectedTaskForDetails.description.split('\n').filter(point => point.trim()).map((point, pointIndex) => (
                        <div key={pointIndex} className="mb-2">
                          <span className="font-medium text-gray-700">{pointIndex + 1}.</span> 
                          <span className="ml-2 text-gray-900">{point.trim()}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-gray-900">
                        {selectedTaskForDetails.description || 'Sin descripción'}
                      </div>
                    )}
                  </div>
                </div>

                {/* Evidencias y archivos */}
                {(selectedTaskForDetails.evidenceFile || selectedTaskForDetails.audioFile || selectedTaskForDetails.evidenceLink) && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">📎 Evidencias y Archivos</h4>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                      {selectedTaskForDetails.evidenceFile && (
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">🖼️</span>
                          <div className="flex-1">
                            <p className="font-medium text-blue-900">{selectedTaskForDetails.evidenceFileName}</p>
                            <p className="text-sm text-blue-700">Imagen • {Math.round(selectedTaskForDetails.evidenceFileSize / 1024)} KB</p>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedImage({
                                url: getFileUrl(selectedTaskForDetails.evidenceFile),
                                name: selectedTaskForDetails.evidenceFileName
                              });
                              setShowImageModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm px-3 py-1 border border-blue-300 rounded"
                          >
                            👁️ Ver
                          </button>
                        </div>
                      )}
                      
                      {selectedTaskForDetails.audioFile && (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-3">
                            <span className="text-2xl">🎵</span>
                            <div className="flex-1">
                              <p className="font-medium text-blue-900">{selectedTaskForDetails.audioFileName}</p>
                              <p className="text-sm text-blue-700">Audio • {Math.round(selectedTaskForDetails.audioFileSize / 1024)} KB</p>
                            </div>
                            <button
                              onClick={() => transcribeAudio(selectedTaskForDetails)}
                              disabled={isTranscribing}
                              className={`text-sm px-3 py-1 border rounded ${
                                isTranscribing 
                                  ? 'text-gray-400 border-gray-300 cursor-not-allowed' 
                                  : 'text-blue-600 hover:text-blue-800 border-blue-300'
                              }`}
                            >
                              {isTranscribing && selectedTaskForTranscription?.id === selectedTaskForDetails.id ? '🔄 Transcribiendo...' : '🎤 Transcribir'}
                            </button>
                          </div>
                          
                          <audio 
                            controls 
                            className="w-full"
                            preload="metadata"
                          >
                            <source src={getFileUrl(selectedTaskForDetails.audioFile)} type="audio/wav" />
                            <source src={getFileUrl(selectedTaskForDetails.audioFile)} type="audio/mpeg" />
                            Tu navegador no soporta el elemento de audio.
                          </audio>
                          
                          {transcriptionResults[selectedTaskForDetails.id] && (
                            <div className="p-3 bg-white border border-blue-200 rounded">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-sm text-blue-700">
                                  {transcriptionResults[selectedTaskForDetails.id].isComplete ? '📝 Transcripción:' : '🔄 Transcribiendo...'}
                                </span>
                                <button
                                  onClick={() => clearTranscription(selectedTaskForDetails.id)}
                                  className="text-red-600 hover:text-red-800 text-xs"
                                >
                                  ✕
                                </button>
                              </div>
                              <div className="text-sm text-blue-800">
                                {transcriptionResults[selectedTaskForDetails.id].final}
                                {transcriptionResults[selectedTaskForDetails.id].interim && (
                                  <span className="text-blue-500 italic"> {transcriptionResults[selectedTaskForDetails.id].interim}</span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {selectedTaskForDetails.evidenceLink && (
                        <div className="flex items-center space-x-3">
                          <span className="text-2xl">🔗</span>
                          <div className="flex-1">
                            <p className="font-medium text-blue-900">Enlace de evidencia</p>
                            <a href={selectedTaskForDetails.evidenceLink} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                              {selectedTaskForDetails.evidenceLink}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Comentarios */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">💬 Comentarios</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    {/* Lista de comentarios existentes */}
                    <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                      {taskComments[selectedTaskForDetails.id] && taskComments[selectedTaskForDetails.id].length > 0 ? (
                        (() => {
                          // Separar comentarios principales y respuestas usando parent_comment_id
                          const mainComments = [];
                          const replies = {};

                          // Primero, ordenar todos los comentarios por fecha de creación
                          const sortedComments = [...taskComments[selectedTaskForDetails.id]].sort((a, b) => {
                            const dateA = new Date(a.createdAt || a.created_at);
                            const dateB = new Date(b.createdAt || b.created_at);
                            return dateA - dateB;
                          });

                          sortedComments.forEach(comment => {
                            // Usar parent_comment_id para determinar si es una respuesta
                            const parentId = comment.parent_comment_id || comment.parentCommentId;

                            if (parentId) {
                              // Es una respuesta - agrupar por parent_comment_id
                              if (!replies[parentId]) replies[parentId] = [];
                              replies[parentId].push(comment);
                            } else {
                              // Es un comentario principal
                              mainComments.push(comment);
                            }
                          });
                          
                          return mainComments.map((comment, index) => (
                            <div key={comment.id || index} className="space-y-2">
                              {/* Comentario principal */}
                              <div className="bg-white p-3 rounded border">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-medium text-gray-900">
                                    👤 {comment.userName || comment.user_name || 'Usuario Anónimo'}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {formatDateOnly(comment.createdAt || comment.created_at)} {new Date(comment.createdAt || comment.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                                
                                {/* Texto del comentario con enlaces clickeables */}
                                {comment.comment && (
                                  <div className="text-gray-700 mb-2">
                                    {renderTextWithLinks(comment.comment)}
                                  </div>
                                )}
                                
                                {/* Archivo adjunto si existe */}
                                {(comment.fileName || comment.file_name) && (
                                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                    <div className="flex items-center space-x-2">
                                      <span className="text-blue-600">📎</span>
                                      <span className="text-sm font-medium text-blue-800">Archivo adjunto:</span>
                                      <span className="text-sm text-blue-700">{comment.fileName || comment.file_name}</span>
                                      {(comment.filePath || comment.file_path) && (
                                          <a 
                                            href={getFileUrl(comment.filePath || comment.file_path)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                          >
                                            📥 Descargar
                                          </a>
                                      )}
                                    </div>
                                    
                                    {/* Mostrar vista previa si es imagen */}
                                    {(comment.filePath || comment.file_path) && (comment.fileName || comment.file_name) && 
                                     (comment.fileName || comment.file_name).toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                                      <div className="mt-2">
                                          <img 
                                            src={getFileUrl(comment.filePath || comment.file_path)}
                                            alt={comment.fileName || comment.file_name}
                                            className="max-w-32 max-h-32 object-cover rounded border cursor-pointer hover:opacity-80"
                                            onClick={() => {
                                              setSelectedImage({
                                                url: getFileUrl(comment.filePath || comment.file_path),
                                                name: comment.fileName || comment.file_name
                                              });
                                              setShowImageModal(true);
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Respuestas al comentario principal */}
                              {replies[comment.id] && replies[comment.id].length > 0 && (
                                <div className="ml-6 space-y-2">
                                  {replies[comment.id].map((reply, replyIndex) => (
                                    <div key={reply.id || `reply-${replyIndex}`} className="bg-gray-50 p-3 rounded border-l-4 border-blue-300">
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="font-medium text-gray-800 flex items-center">
                                          <span className="text-blue-600 mr-1">↳</span>
                                          👤 {reply.userName || reply.user_name || 'Usuario Anónimo'}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {formatDateOnly(reply.createdAt || reply.created_at)} {new Date(reply.createdAt || reply.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                      </div>
                                      
                                      {/* Texto de la respuesta con enlaces clickeables */}
                                      {reply.comment && (
                                        <div className="text-gray-700 mb-2">
                                          {renderTextWithLinks(reply.comment)}
                                        </div>
                                      )}
                                      
                                      {/* Archivo adjunto en respuesta si existe */}
                                      {(reply.fileName || reply.file_name) && (
                                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                          <div className="flex items-center space-x-2">
                                            <span className="text-blue-600">📎</span>
                                            <span className="text-sm font-medium text-blue-800">Archivo adjunto:</span>
                                            <span className="text-sm text-blue-700">{reply.fileName || reply.file_name}</span>
                                            {(reply.filePath || reply.file_path) && (
                                                <a 
                                                  href={getFileUrl(reply.filePath || reply.file_path)}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                                                >
                                                  📥 Descargar
                                                </a>
                                            )}
                                          </div>
                                          
                                          {/* Vista previa de imagen en respuesta */}
                                          {(reply.filePath || reply.file_path) && (reply.fileName || reply.file_name) && 
                                           (reply.fileName || reply.file_name).toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i) && (
                                            <div className="mt-2">
                                                <img 
                                                  src={getFileUrl(reply.filePath || reply.file_path)}
                                                  alt={reply.fileName || reply.file_name}
                                                  className="max-w-24 max-h-24 object-cover rounded border cursor-pointer hover:opacity-80"
                                                  onClick={() => {
                                                    setSelectedImage({
                                                      url: getFileUrl(reply.filePath || reply.file_path),
                                                      name: reply.fileName || reply.file_name
                                                    });
                                                    setShowImageModal(true);
                                                  }}
                                                  onError={(e) => {
                                                    e.target.style.display = 'none';
                                                  }}
                                                />
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ));
                        })()
                      ) : (
                        <div className="text-gray-500 text-center py-4">No hay comentarios para esta tarea</div>
                      )}
                    </div>
                    
                    {/* Formulario para nuevo comentario */}
                    <div className="border-t border-gray-200 pt-4">
                      <div className="space-y-3">
                        <textarea
                          value={newComment[selectedTaskForDetails.id] || ''}
                          onChange={(e) => setNewComment(prev => ({ ...prev, [selectedTaskForDetails.id]: e.target.value }))}
                          rows="3"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Escribe un comentario sobre esta tarea..."
                        />
                        
                        {/* Sección para subir archivos */}
                        <div className="flex items-center space-x-2">
                          <input
                            type="file"
                            id={`commentFile-${selectedTaskForDetails.id}`}
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                handleCommentFileSelect(selectedTaskForDetails.id, file);
                              }
                            }}
                            className="hidden"
                            accept="image/*,application/pdf,.doc,.docx,.txt"
                          />
                          <button
                            type="button"
                            onClick={() => document.getElementById(`commentFile-${selectedTaskForDetails.id}`).click()}
                            className="text-sm px-3 py-1 border border-gray-300 rounded text-gray-600 hover:text-gray-800 hover:bg-gray-50"
                          >
                            📎 Adjuntar Archivo
                          </button>
                          
                          {/* Mostrar archivo seleccionado */}
                          {commentFiles[selectedTaskForDetails.id] && (
                            <div className="flex items-center space-x-2 text-sm">
                              <span className="text-gray-600">
                                📄 {commentFiles[selectedTaskForDetails.id].name}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeCommentFile(selectedTaskForDetails.id)}
                                className="text-red-600 hover:text-red-800"
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex justify-end">
                          <button
                            onClick={() => submitComment(selectedTaskForDetails.id)}
                            disabled={submittingComment[selectedTaskForDetails.id] || (!newComment[selectedTaskForDetails.id]?.trim() && !commentFiles[selectedTaskForDetails.id])}
                            className={`px-4 py-2 rounded-md ${
                              submittingComment[selectedTaskForDetails.id] || (!newComment[selectedTaskForDetails.id]?.trim() && !commentFiles[selectedTaskForDetails.id])
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            {submittingComment[selectedTaskForDetails.id] ? '📤 Enviando...' : '📤 Enviar Comentario'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Acciones */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedTaskForDetails.completed || false}
                        onChange={(e) => handleTaskCompletion(selectedTaskForDetails.id, selectedTaskForDetails.meetingId, selectedTaskForDetails.clientId, e.target.checked, selectedTaskForDetails.completionComment || '')}
                        className="h-4 w-4 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">Marcar como realizada</span>
                    </label>
                    
                    {currentUser?.role === 'admin' && (selectedTaskForDetails.completed || selectedTaskForDetails.status === 'completada') && (
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={Boolean(selectedTaskForDetails.approved) || selectedTaskForDetails.status === 'aprobada'}
                          onChange={async (e) => {
                            const isApproved = e.target.checked;
                            
                            // Actualizar inmediatamente el estado del modal
                            setSelectedTaskForDetails(prev => ({
                              ...prev,
                              approved: isApproved,
                              status: isApproved ? 'aprobada' : (prev.completed ? 'completada' : 'pendiente')
                            }));
                            
                            // Llamar a la función de aprobación
                            await handleTaskApproval(selectedTaskForDetails.id, selectedTaskForDetails.meetingId, selectedTaskForDetails.clientId, isApproved);
                          }}
                          className="h-4 w-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm text-purple-700">Aprobar tarea</span>
                      </label>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => {
                        setEditingTask({
                          ...selectedTaskForDetails,
                          clientId: selectedTaskForDetails.clientId,
                          meetingId: selectedTaskForDetails.meetingId,
                          dueDate: selectedTaskForDetails.dueDate ? new Date(selectedTaskForDetails.dueDate).toISOString().split('T')[0] : '',
                          priority: selectedTaskForDetails.priority || 'media',
                          status: selectedTaskForDetails.status || (selectedTaskForDetails.completed ? 'completada' : 'pendiente'),
                          cell: selectedTaskForDetails.cell || '',
                          completionComment: selectedTaskForDetails.completionComment || ''
                        });
                        setShowTaskDetailsModal(false);
                        setShowEditTaskModal(true);
                      }}
                      className="text-gray-600 hover:text-gray-800 text-sm px-3 py-1 border border-gray-300 rounded-md"
                    >
                      ✏️ Editar
                    </button>
                    
                    <button 
                      onClick={() => {
                        setShowTaskDetailsModal(false);
                        setSelectedTaskForDetails(null);
                      }}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    >
                      Cerrar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Imagen */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <button 
              onClick={() => {
                setShowImageModal(false);
                setSelectedImage(null);
              }}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2 z-10"
            >
              ✕
            </button>
            <img 
              src={selectedImage.url}
              alt={selectedImage.name}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                console.error('Error loading image:', selectedImage.url);
                alert('Error al cargar la imagen');
                setShowImageModal(false);
                setSelectedImage(null);
              }}
            />
            <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-3 py-2 rounded-md">
              {selectedImage.name}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllTasksSection;
